import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import CloseIcon from "@mui/icons-material/Close";
import * as Yup from "yup";
import { loadStripe } from "@stripe/stripe-js";
import validator from "validator";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonIcon from "@mui/icons-material/Person";
import SecurityIcon from "@mui/icons-material/Security";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PaymentIcon from "@mui/icons-material/Payment";
import SendIcon from "@mui/icons-material/Send";

import {
  Modal,
  Box,
  Typography,
  IconButton,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Switch,
  Grid2 as Grid,
  TextField,
  Checkbox,
  Button,
  Link,
  Chip,
  Alert,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  InputLabel,
  FormHelperText,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useFormik } from "formik";
import { styled } from "@mui/material/styles";
import CreditCardDisplay from "../noc/CreditCardDisplay";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import {
  getPermitPackages,
  updatePermitPackage,
} from "../../store/actions/dataActions";
import { formatMoneyValue } from "../../functions/permitFormHelpers";
import {
  setActiveTab,
  setModalsState,
  setPage,
  setRowCount,
  setSelectedRow,
} from "../../store/actions/uiActions/myPermitsActions";

// Styled Components
const AnimatedModal = styled(Modal)(({ theme }) => ({
  "& .MuiBackdrop-root": {
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    backdropFilter: "none",
  },
}));

const ModalContainer = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  height: "90vh",
  maxHeight: "850px",
  width: "85vw",
  maxWidth: "1100px",
  backgroundColor: theme.palette.background.paper,
  borderRadius: "16px",
  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
}));

const ModalHeader = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: theme.palette.primary.contrastText,
  padding: theme.spacing(2),
  position: "relative",
  overflow: "hidden",
  flexShrink: 0,
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      "linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.08) 50%, transparent 70%)",
    // Disable shimmer animation to reduce paint work on open
    animation: "none",
  },
}));

const StepCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== "active",
})(({ theme, active }) => ({
  marginBottom: theme.spacing(2),
  transition: "all 0.3s ease",
  cursor: "pointer",
  border: active
    ? `2px solid ${theme.palette.primary.main}`
    : `2px solid transparent`,
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: theme.shadows[8],
  },
  ...(active && {
    background: `linear-gradient(135deg, ${theme.palette.primary.light}15 0%, ${theme.palette.primary.main}08 100%)`,
  }),
}));

const AnimatedStep = styled(Box)(() => ({
  minHeight: "450px",
  display: "flex",
  flexDirection: "column",
}));

const ProgressIndicator = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(1.5),
  background: `linear-gradient(135deg, ${theme.palette.grey[50]} 0%, ${theme.palette.grey[100]} 100%)`,
  borderRadius: "8px",
  border: `1px solid ${theme.palette.grey[200]}`,
  flexShrink: 0,
}));

const StepIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== "completed" && prop !== "active",
})(({ theme, completed, active }) => ({
  width: 48,
  height: 48,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginRight: theme.spacing(2),
  transition: "all 0.3s ease",
  backgroundColor: completed
    ? theme.palette.success.main
    : active
      ? theme.palette.primary.main
      : theme.palette.grey[300],
  color:
    completed || active
      ? theme.palette.primary.contrastText
      : theme.palette.grey[600],
  // Remove pulsing animation to reduce layout/paint cycles
}));

const FloatingActionButton = styled(LoadingButton)(({ theme }) => ({
  borderRadius: "8px",
  padding: theme.spacing(0.75, 2.5),
  fontSize: "0.95rem",
  fontWeight: 600,
  textTransform: "none",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  transition: "all 0.2s ease",
  "&:hover": {
    transform: "translateY(-1px)",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
  },
}));

function StartNotarizationModal({
  open,
  setOpen,
  setReviewDocModalOpen,
  setSnackOpen,
  setRonScheduleOpen,
  docNotaryTypeValue,
  // setAddPaymentModalOpen,
}) {
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const currentUser = useSelector((state) => state[currentSession].user);
  const { selectedRow } = useSelector((state) => state.myPermitsComponent);

  const dispatch = useDispatch();

  console.log(currentUser);

  // Core state
  const [signingOrder, setSigningOrder] = useState("contractor");
  const [autoStartRecording, setAutoStartRecording] = useState(true);
  const [activeStep, setActiveStep] = useState(0);
  const [notaryPaymentLoading, setNotaryPaymentLoading] = useState(false);
  const [isAcknowledged, setIsAcknowledged] = useState(false);

  // Wizard state
  const [currentWizardStep, setCurrentWizardStep] = useState(0);
  const [selectedNotary, setSelectedNotary] = useState(null);
  const [availableNotaries, setAvailableNotaries] = useState([]);
  const [submitError, setSubmitError] = useState("");
  const [tempAmount, setTempAmount] = useState(null);
  const [paymentCards, setPaymentCards] = useState([]);
  const [confirmedPricing, setConfirmedPricing] = useState(null);

  // Signing requirements
  const [ownerSigning, setOwnerSigning] = useState({
    notarizationNeeded: false,
    signingNeeded: false,
  });
  const [contractorSigning, setContractorSigning] = useState({
    notarizationNeeded: false,
    signingNeeded: false,
  });

  console.log("contractorSigning: ", contractorSigning);

  // IPEN & Notary Pool states - use refs to persist across selectedRow updates during auto-save
  const notarizationTypeRef = useRef("ron");
  const ownerNotarizationTypeRef = useRef("ron");
  const notarySourceRef = useRef("permitRockstar");
  const ipenPinRef = useRef("");
  const ownerIpenNotaryIdRef = useRef("");

  const [notarizationType, setNotarizationTypeState] = useState("ron");
  const [ownerNotarizationType, setOwnerNotarizationTypeState] =
    useState("ron");
  // Single contractor state (for non-St. Johns counties)
  const [contractorNotarizationType, setContractorNotarizationTypeState] =
    useState("ron");
  const [contractorIpenNotaryId, setContractorIpenNotaryIdState] = useState("");
  // Store contractor settings per contractor (keyed by license number) - only for St. Johns
  const [contractorSettings, setContractorSettings] = useState({}); // { licenseNumber: { notarizationType: "ron", ipenNotaryId: "", selectedUser: "" } }
  const [notarySource, setNotarySourceState] = useState("permitRockstar");
  const [ipenPin, setIpenPinState] = useState("");
  const [hasOrgNotaries, setHasOrgNotaries] = useState(false);
  const [canUseIpen, setCanUseIpen] = useState(false);
  const [ownerIpenNotaryId, setOwnerIpenNotaryIdState] = useState("");

  const [requiredContractors, setRequiredContractors] = useState([]);
  const [currentContractorIndex, setCurrentContractorIndex] = useState(0);
  const [contractorsSeen, setContractorsSeen] = useState(new Set());
  // Store form values per contractor (keyed by license number) to preserve user edits

  console.log("currentUser ", currentUser);
  console.log("selectedRow ", selectedRow);

  useEffect(() => {
    if (selectedRow?.county?.toLowerCase() !== "st johns") return;

    let foundContractors = [];
    const seenSelectedUsers = new Set();

    const contractors = Object.values(selectedRow?.documents ?? {})
      .map((d) => d?.contractor)
      .filter(Boolean);

    if (contractors.length <= 1) {
      foundContractors.push(selectedRow?.contractor);
      setContractorsSeen(true);
    } else {
      if (Array.isArray(currentUser?.user?.contractors)) {
        currentUser?.user?.contractors?.forEach((c) => {
          if (contractors.includes(c?.licenseNumber)) {
            const selectedUser = c?.selectedUser;
            // Only push if selectedUser is unique (not seen before)
            if (selectedUser && !seenSelectedUsers.has(selectedUser)) {
              seenSelectedUsers.add(selectedUser);
              foundContractors.push(c);
            } else if (!selectedUser) {
              // If no selectedUser, allow it (might be main user's contractor)
              foundContractors.push(c);
            }
          }
        });
      }
    }

    setRequiredContractors(foundContractors);
    // Reset to first contractor when contractors change
    setCurrentContractorIndex(0);
    // Reset viewed contractors - mark first one as seen
    if (foundContractors.length > 0) {
      setContractorsSeen(new Set([0]));
      // Initialize contractor settings with default values for all contractors
      setContractorSettings((prev) => {
        const updated = { ...prev };
        foundContractors.forEach((contractor) => {
          const license = contractor?.licenseNumber;
          if (license && !updated[license]) {
            updated[license] = {
              notarizationType: "ron",
              ipenNotaryId: "",
              selectedUser: contractor?.selectedUser || "",
            };
          } else if (
            license &&
            updated[license] &&
            !updated[license].selectedUser
          ) {
            // Preserve existing settings but add selectedUser if missing
            updated[license] = {
              ...updated[license],
              selectedUser: contractor?.selectedUser || "",
            };
          }
        });
        return updated;
      });
    } else {
      setContractorsSeen(new Set());
      setContractorSettings({});
    }
  }, [
    currentUser?.user?.contractors,
    selectedRow?.contractor,
    selectedRow?.county,
    selectedRow?.documents,
    open,
  ]);

  console.log("requiredContractors: ", requiredContractors);

  // Wrapper setters that update both state and refs
  const setNotarizationType = (value) => {
    notarizationTypeRef.current = value;
    setNotarizationTypeState(value);
  };
  const setOwnerNotarizationType = (value) => {
    ownerNotarizationTypeRef.current = value;
    setOwnerNotarizationTypeState(value);
  };
  // Helper function to check if St. Johns county
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isStJohnsCounty = useCallback(() => {
    return (
      selectedRow?.county?.toLowerCase() === "st johns" ||
      selectedRow?.county?.toLowerCase() === "st_johns" ||
      selectedRow?.county?.toLowerCase()?.includes("st johns")
    );
  }, [selectedRow?.county]);

  // Helper functions to get/set contractor settings (only for St. Johns)
  const getCurrentContractorLicense = () => {
    if (!isStJohnsCounty()) return "";
    return requiredContractors[currentContractorIndex]?.licenseNumber || "";
  };

  const getContractorNotarizationType = () => {
    if (!isStJohnsCounty()) {
      return contractorNotarizationType;
    }
    const license = getCurrentContractorLicense();
    return contractorSettings[license]?.notarizationType || "ron";
  };

  const getContractorIpenNotaryId = () => {
    if (!isStJohnsCounty()) {
      return contractorIpenNotaryId;
    }

    // For St. Johns with multiple contractors: return the shared notary (from first contractor that has one)
    if (requiredContractors.length > 1) {
      // Find the first contractor that has a notary selected
      for (const contractor of requiredContractors) {
        const license = contractor?.licenseNumber;
        if (license && contractorSettings[license]?.ipenNotaryId) {
          return contractorSettings[license].ipenNotaryId;
        }
      }
      return "";
    }

    // Single contractor: return that contractor's notary
    const license = getCurrentContractorLicense();
    return contractorSettings[license]?.ipenNotaryId || "";
  };

  const setContractorNotarizationType = (value) => {
    if (!isStJohnsCounty()) {
      setContractorNotarizationTypeState(value);
      return;
    }
    const license = getCurrentContractorLicense();
    if (!license) return;

    setContractorSettings((prev) => ({
      ...prev,
      [license]: {
        ...prev[license],
        notarizationType: value,
      },
    }));
  };

  const setContractorIpenNotaryId = (value) => {
    if (!isStJohnsCounty()) {
      setContractorIpenNotaryIdState(value);
      return;
    }

    // For St. Johns with multiple contractors: apply the same notary to all contractors
    if (requiredContractors.length > 1) {
      setContractorSettings((prev) => {
        const updated = { ...prev };
        // Update all contractors with the same notary
        requiredContractors.forEach((contractor) => {
          const license = contractor?.licenseNumber;
          if (license) {
            updated[license] = {
              ...updated[license],
              ipenNotaryId: value,
            };
          }
        });
        return updated;
      });
    } else {
      // Single contractor: update only that contractor
      const license = getCurrentContractorLicense();
      if (!license) return;

      setContractorSettings((prev) => ({
        ...prev,
        [license]: {
          ...prev[license],
          ipenNotaryId: value,
        },
      }));
    }
  };
  const setNotarySource = (value) => {
    notarySourceRef.current = value;
    setNotarySourceState(value);
  };
  const setIpenPin = (value) => {
    ipenPinRef.current = value;
    setIpenPinState(value);
  };
  const setOwnerIpenNotaryId = (value) => {
    ownerIpenNotaryIdRef.current = value;
    setOwnerIpenNotaryIdState(value);
  };
  // Restore state from refs when selectedRow changes but modal is still open
  // This prevents state loss during auto-save updates
  useEffect(() => {
    if (open && selectedRow) {
      // Restore values from refs if they differ from current state
      if (notarizationTypeRef.current !== notarizationType) {
        setNotarizationTypeState(notarizationTypeRef.current);
      }
      if (ownerNotarizationTypeRef.current !== ownerNotarizationType) {
        setOwnerNotarizationTypeState(ownerNotarizationTypeRef.current);
      }
      if (notarySourceRef.current !== notarySource) {
        setNotarySourceState(notarySourceRef.current);
      }
      if (ipenPinRef.current !== ipenPin) {
        setIpenPinState(ipenPinRef.current);
      }
      if (ownerIpenNotaryIdRef.current !== ownerIpenNotaryId) {
        setOwnerIpenNotaryIdState(ownerIpenNotaryIdRef.current);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    selectedRow?.task_id, // Only react to task_id changes, not entire object
    notarizationType,
    ownerNotarizationType,
    notarySource,
    ipenPin,
    ownerIpenNotaryId,
  ]);

  // Service selection states
  const [planReviewService, setPlanReviewService] = useState({
    usePermitRockstar: false,
    useOrganization: false,
    customProvider: "",
  });
  const [inspectionService, setInspectionService] = useState({
    usePermitRockstar: false,
    useOrganization: false,
    customProvider: "",
    inspectionTypes: [],
  });

  // Loading states
  const [loadingNotaries, setLoadingNotaries] = useState(false);
  const [signOnly, setSignOnly] = useState(false);

  const ownerSigningRef = useRef(ownerSigning);
  const contractorSigningRef = useRef(contractorSigning);

  useEffect(() => {
    // Check if all documents are sign-only (only sign: true, all other properties false)
    if (
      !selectedRow?.selected_documents ||
      selectedRow.selected_documents.length === 0
    ) {
      return;
    }

    let isSignOnly = true;

    // Iterate through all selected documents
    for (const document of selectedRow.selected_documents) {
      const signatures = document?.signatures;

      if (!signatures) {
        continue;
      }

      // Iterate through all signature parties (owner, contractor, private_provider, examiner, etc.)
      for (const [partyName, partyData] of Object.entries(signatures)) {
        // Skip 'order' and other non-party properties
        if (
          partyName === "order" ||
          typeof partyData !== "object" ||
          partyData === null
        ) {
          continue;
        }

        // Only check parties that have sign: true
        // If sign is false, we don't care about other properties
        if (partyData.sign !== true) {
          continue;
        }

        // For parties with sign: true, check all other properties (notary, etc.)
        // They should all be false for it to be sign-only
        const otherProperties = Object.entries(partyData).filter(
          ([key]) => key !== "sign",
        );

        const hasOtherTrueValues = otherProperties.some(
          ([, value]) => value === true,
        );

        // If sign is true but any other property is also true, it's not sign-only
        if (hasOtherTrueValues) {
          isSignOnly = false;
          break;
        }
      }

      // If we found a document that's not sign-only, break early
      if (!isSignOnly) {
        break;
      }
    }

    setSignOnly(isSignOnly);
  }, [selectedRow]);

  useEffect(() => {
    ownerSigningRef.current = ownerSigning;
  }, [ownerSigning]);

  useEffect(() => {
    contractorSigningRef.current = contractorSigning;
  }, [contractorSigning]);

  const mapping = useMemo(() => ["notary_record", "noc_notary"], []);

  // Helper function to get readable document names
  const getDocumentDisplayName = useCallback((docName) => {
    const nameMap = {
      noc: "Notice of Commencement",
      noc_affidavit: "Notice of Commencement Affidavit",
      permit_application: "Permit Application",
      building_permit: "Building Permit",
      private_provider: "Private Provider Agreement",
      pp_ntbo_cover_sheet: "Private Provider NTBO Cover Sheet",
      pp_ntbo: "Private Provider NTBO",
      contractor_authorization: "Contractor Authorization",
      property_owner_authorization: "Property Owner Authorization",
      lien_release: "Lien Release",
      insurance_certificate: "Insurance Certificate",
    };
    return (
      nameMap[docName] ||
      docName.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
    );
  }, []);

  // Get filtered documents based on docNotaryTypeValue
  const getFilteredDocuments = useCallback(() => {
    let selected_documents = [];

    if (docNotaryTypeValue === 0) {
      selected_documents = selectedRow?.selected_documents || [];
    } else if (docNotaryTypeValue === 1) {
      selected_documents =
        selectedRow?.selected_documents?.filter(
          (doc) => doc.name === "noc" || doc.name === "noc_affidavit",
        ) || [];
    } else if (docNotaryTypeValue === 2) {
      selected_documents =
        selectedRow?.selected_documents?.filter((doc) => doc.name !== "noc") ||
        [];
    }

    return selected_documents;
  }, [docNotaryTypeValue, selectedRow?.selected_documents]);

  // Memoize and defer document list to reduce work during initial open
  const filteredDocsMemo = React.useMemo(
    () => getFilteredDocuments(),
    [getFilteredDocuments],
  );
  const deferredFilteredDocs = React.useDeferredValue(filteredDocsMemo);

  // Wizard configuration - base steps
  const baseWizardSteps = useMemo(
    () => [
      {
        id: "overview",
        title: "Document Overview",
        icon: <AssignmentIcon />,
        description: "Review document requirements and signing order",
      },
      {
        id: "services",
        title: "Notarization Method",
        icon: <SecurityIcon />,
        description: "Choose notarization method for each party",
      },
      {
        id: "information",
        title: "Signer Information",
        icon: <PersonIcon />,
        description: "Provide signer details",
      },
      {
        id: "payment",
        title: "Payment & Confirmation",
        icon: <PaymentIcon />,
        description: "Complete payment and confirm details",
      },
    ],
    [],
  );

  // Filter steps based on whether notarization is needed
  const needsNotarization =
    contractorSigning?.notarizationNeeded || ownerSigning?.notarizationNeeded;
  const wizardSteps = useMemo(() => {
    if (needsNotarization) {
      return baseWizardSteps;
    }
    // If only signing is needed, skip notarization method and notary steps
    return baseWizardSteps.filter((step) => !step.requiresNotarization);
  }, [needsNotarization, baseWizardSteps]);

  // Get instruction text based on signing requirements
  const getInstructionText = useCallback(() => {
    const ownerHasAction =
      ownerSigning?.notarizationNeeded || ownerSigning?.signingNeeded;
    const contractorHasNotarization = contractorSigning?.notarizationNeeded;

    if (contractorHasNotarization && ownerHasAction) {
      if (selectedRow?.documents?.noc_affidavit?.is_created) {
        return "Both parties have actions. Because the Notice of Commencement affidavit is included, the owner must complete their notarization first; the contractor proceeds after.";
      }
      return "Both parties have actions. Choose who signs first and the other party will be invited automatically after the first completes.";
    }

    if (contractorHasNotarization && !ownerHasAction) {
      return "Only the contractor needs to sign and notarize the required documents. The owner/agent has no actions for these PDFs.";
    }

    if (!ownerHasAction && !contractorSigning?.signingNeeded) {
      return "Contractors are only required to sign documents. Upon providing some basic information, you can begin signing your documents.";
    }

    if (
      !contractorHasNotarization &&
      contractorSigning?.signingNeeded &&
      ownerHasAction
    ) {
      return "Contractors only need to e‑sign. After they sign, we will email the owner/agent to complete Remote Online Notarization.";
    }

    if (!contractorHasNotarization && !contractorSigning?.signingNeeded) {
      return "Only the owner/agent needs to complete notarization. We will send them an email to schedule Remote Online Notarization.";
    }

    return "Please review the document requirements and select your preferred notarization method.";
  }, [
    ownerSigning,
    contractorSigning,
    selectedRow?.documents?.noc_affidavit?.is_created,
  ]);

  useEffect(() => {
    const hasNotaries = (currentUser?.tier?.notary?.num_notaries || 0) > 0;
    setHasOrgNotaries(hasNotaries);
  }, [currentUser]);

  useEffect(() => {
    let ownerNotarizationNeeded = false;
    let ownerSigningNeeded = false;
    let contractorNotarizationNeeded = false;
    let contractorSigningNeeded = false;

    let selected_documents = [];

    if (docNotaryTypeValue === 0) {
      selected_documents = selectedRow?.selected_documents || [];
    } else if (docNotaryTypeValue === 1) {
      selected_documents =
        selectedRow?.selected_documents?.filter(
          (doc) => doc.name === "noc" || doc.name === "noc_affidavit",
        ) || [];
    } else if (docNotaryTypeValue === 2) {
      selected_documents =
        selectedRow?.selected_documents?.filter((doc) => doc.name !== "noc") ||
        [];
    }

    // Check if pp_ntbo is in selected documents and get its signerType
    const ppNtboSignerType =
      selectedRow?.documents?.pp_ntbo?.sections?.[1]?.fields?.signerType;

    selected_documents.forEach((doc) => {
      // Special handling for pp_ntbo - use signerType to determine who signs
      if (doc.name === "pp_ntbo" && ppNtboSignerType) {
        if (ppNtboSignerType === "feeOwner") {
          // Owner signs pp_ntbo
          ownerSigningNeeded = true;
        } else if (ppNtboSignerType === "feeContractor") {
          // Contractor signs pp_ntbo
          contractorSigningNeeded = true;
        }
      } else {
        // Normal handling for other documents
        if (doc.signatures?.owner?.sign) ownerSigningNeeded = true;
        if (doc.signatures?.owner?.notary) ownerNotarizationNeeded = true;
        if (doc.signatures?.contractor?.sign) contractorSigningNeeded = true;
        if (doc.signatures?.contractor?.notary)
          contractorNotarizationNeeded = true;
      }
    });

    const newOwner = {
      notarizationNeeded: ownerNotarizationNeeded,
      signingNeeded: ownerSigningNeeded,
    };

    const newContractor = {
      notarizationNeeded: contractorNotarizationNeeded,
      signingNeeded: contractorSigningNeeded,
    };

    if (JSON.stringify(ownerSigningRef.current) !== JSON.stringify(newOwner)) {
      setOwnerSigning(newOwner);
    }

    if (
      JSON.stringify(contractorSigningRef.current) !==
      JSON.stringify(newContractor)
    ) {
      setContractorSigning(newContractor);
    }
  }, [docNotaryTypeValue, selectedRow, open]);

  useEffect(() => {
    let cards = [];

    if (currentUser?.payment_cards?.length > 0) {
      cards = currentUser.payment_cards.map((card) => {
        const lastTwo = String(card.exp_year).slice(-2);
        const first = card.exp_month;

        return {
          id: card.id,
          cardNum: "**** **** **** " + card.last4,
          cardName: card.name ? card.name : card.card_holder,
          expires:
            (first < 10 ? "0" + String(first) : String(first)) + "/" + lastTwo,
          default: card.default,
        };
      });
      setPaymentCards(cards);
    }
  }, [currentUser.payment_cards]);

  useEffect(() => {
    // Reset confirmed pricing when document type changes
    setConfirmedPricing(null);

    if ([0, 1].includes(docNotaryTypeValue)) {
      if (
        currentUser?.tier?.discount?.[mapping[docNotaryTypeValue]]?.active &&
        ((currentUser?.tier?.discount?.[mapping[docNotaryTypeValue]]
          ?.duration === "once" &&
          currentUser?.tier?.discount?.[mapping[docNotaryTypeValue]]?.used ===
            false) ||
          currentUser?.tier?.discount?.[mapping[docNotaryTypeValue]]
            ?.duration === "forever")
      ) {
        const originalPrice = parseFloat(
          currentUser?.service_tiers?.[mapping[docNotaryTypeValue]],
        );
        const discountAmount = parseFloat(
          currentUser?.tier?.discount?.[mapping[docNotaryTypeValue]]?.amount,
        );

        // Ensure the final price is never negative
        const newPrice = Math.max(0, originalPrice - discountAmount);

        setTempAmount(formatMoneyValue(newPrice));
      } else {
        if (!notaryPaymentLoading) {
          setTempAmount(null);
        }
      }
    }
  }, [
    docNotaryTypeValue,
    notaryPaymentLoading,
    mapping,
    currentUser?.service_tiers,
    currentUser?.tier?.discount,
  ]);

  // Update signing order based on requirements
  useEffect(() => {
    const ownerRequired =
      ownerSigning?.notarizationNeeded || ownerSigning?.signingNeeded;
    const contractorRequired =
      contractorSigning?.notarizationNeeded || contractorSigning?.signingNeeded;

    if (ownerRequired && !contractorRequired) {
      setSigningOrder("owner");
      return;
    }
    if (!ownerRequired && contractorRequired) {
      setSigningOrder("contractor");
      return;
    }

    // Edge case: NOC affidavit present forces owner first when both required
    if (
      contractorSigning?.notarizationNeeded &&
      selectedRow?.documents?.noc_affidavit?.is_created
    ) {
      setSigningOrder("owner");
      return;
    }
  }, [
    ownerSigning,
    contractorSigning,
    selectedRow?.documents?.noc_affidavit?.is_created,
  ]);

  const handleNewPhoneChange = (event) => {
    const { value, name } = event.target;

    // Remove all non-numeric characters
    const digitsOnly = value.replace(/\D/g, "");

    // Limit to 10 digits (XXX-XXX-XXXX format)
    if (digitsOnly.length > 10) {
      return; // Don't update if more than 10 digits
    }

    let formattedPhoneNumber = "";

    // Format as XXX-XXX-XXXX
    for (let i = 0; i < digitsOnly.length; i++) {
      if (i === 3 || i === 6) {
        formattedPhoneNumber += "-";
      }
      formattedPhoneNumber += digitsOnly[i];
    }

    formik.setFieldValue(name, formattedPhoneNumber); // Update formik field value
  };

  function convertDayjsToObject(dayjsObj) {
    return {
      day_of_month: dayjsObj.date(),
      month: dayjsObj.month() + 1,
      year: dayjsObj.year(),
      time: dayjsObj.format("HH:mm"),
    };
  }

  useEffect(() => {
    if (submitError) {
      const interval = setTimeout(() => {
        setSubmitError("");
      }, 4000);

      return () => clearTimeout(interval);
    }
  }, [submitError]);

  // Get available organization notaries
  const getAvailableNotaries = useCallback(async () => {
    if (notarizationType !== "ipen") return;

    setLoadingNotaries(true);
    try {
      const resp = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/nocApi/notary/get-org-notaries`,
        {
          params: { currentSession },
          withCredentials: true,
        },
      );

      const notaries = resp.data.notaries || [];
      setAvailableNotaries(notaries);

      // If we found notaries but hasOrgNotaries was false, update it
      if (notaries.length > 0 && !hasOrgNotaries) {
        setHasOrgNotaries(true);
      }
    } catch (error) {
      console.error("Error fetching organization notaries:", error);
      console.error("Error details:", error.response?.data);
      setAvailableNotaries([]);
    } finally {
      setLoadingNotaries(false);
    }
  }, [hasOrgNotaries, notarizationType, currentSession]);

  // Determine if IPEN can be used based on document requirements
  useEffect(() => {
    // IPEN can only be used if:
    // 1. Organization has notaries
    // 2. Owner doesn't need notarization OR it's a mixed scenario

    const ownerOnlyNotarization =
      ownerSigning.notarizationNeeded &&
      !contractorSigning.notarizationNeeded &&
      !contractorSigning.signingNeeded;

    // IPEN is available if org has notaries and it's NOT owner-only notarization
    setCanUseIpen(hasOrgNotaries && !ownerOnlyNotarization);

    // Reset to RON if IPEN becomes unavailable
    if (!hasOrgNotaries && notarizationType === "ipen") {
      setNotarizationType("ron");
    }
  }, [hasOrgNotaries, ownerSigning, contractorSigning, notarizationType]);

  // Reset owner and contractor notarization types to RON if IPEN is selected but org has no notaries
  useEffect(() => {
    if (!hasOrgNotaries) {
      if (ownerNotarizationType === "ipen") {
        setOwnerNotarizationType("ron");
      }
      if (isStJohnsCounty()) {
        // Reset all contractors to RON if IPEN is not available (St. Johns)
        setContractorSettings((prev) => {
          const updated = { ...prev };
          Object.keys(updated).forEach((license) => {
            if (updated[license]?.notarizationType === "ipen") {
              updated[license] = {
                ...updated[license],
                notarizationType: "ron",
                ipenNotaryId: "",
              };
            }
          });
          return updated;
        });
      } else {
        // Reset single contractor to RON if IPEN is not available (other counties)
        if (contractorNotarizationType === "ipen") {
          setContractorNotarizationType("ron");
          setContractorIpenNotaryId("");
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasOrgNotaries,
    ownerNotarizationType,
    contractorNotarizationType,
    selectedRow?.county, // isStJohnsCounty depends on this
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]);

  // Generate IPEN PIN when IPEN is selected
  useEffect(() => {
    const generateIpenPin = async () => {
      if (notarizationType === "ipen" && open && !ipenPin) {
        try {
          const resp = await axios.post(
            `${process.env.REACT_APP_BASE_URL}/nocApi/notary/generate-ipen-pin`,
            {
              task_id: selectedRow?.task_id,
              currentSession: currentSession,
            },
            { withCredentials: true },
          );

          setIpenPin(resp.data.pin);
        } catch (error) {
          console.error("Error generating IPEN PIN:", error);
          // Fallback PIN generation (frontend only, for display) - numbers only
          setIpenPin(Math.floor(100000 + Math.random() * 900000).toString());
        }
      }
    };

    generateIpenPin();
  }, [notarizationType, open, selectedRow?.task_id, currentSession, ipenPin]);

  // Fetch available notaries when IPEN is selected
  useEffect(() => {
    const notaryStepIndex = wizardSteps.findIndex(
      (step) => step.id === "notary",
    );

    if (
      notarizationType === "ipen" &&
      currentWizardStep === notaryStepIndex &&
      notaryStepIndex !== -1
    ) {
      getAvailableNotaries();
    }
  }, [notarizationType, currentWizardStep, getAvailableNotaries, wizardSteps]);

  // Helper function to split full name into first, middle, and last name
  // Name order is now: firstname + middlename + lastname (normalized by backend)
  // Backend now returns names in natural order: "First Middle Last"
  const splitFullName = (fullName) => {
    if (!fullName || typeof fullName !== "string") {
      return { first: "", middle: "", last: "" };
    }

    const trimmedName = fullName.trim();
    if (!trimmedName) {
      return { first: "", middle: "", last: "" };
    }

    // Check if it's a business name (has business indicators like LLC, INC, etc.)
    // Business names should not be split
    const businessKeywords = [
      "INC",
      "LLC",
      "CORP",
      "CORPORATION",
      "COMPANY",
      "CO",
      "LP",
      "LLP",
      "LTD",
      "LIMITED",
      "PARTNERSHIP",
      "PARTNERS",
      "ASSOCIATES",
      "ASSOCIATION",
      "ENTERPRISES",
      "GROUP",
      "HOLDINGS",
      "PROPERTIES",
      "REALTY",
      "TRUST",
      "TRUSTEE",
    ];

    const nameUpper = trimmedName.toUpperCase();
    const isBusiness = businessKeywords.some((keyword) =>
      nameUpper.includes(keyword),
    );

    // If it's a business name, don't split it - use as full name
    if (isBusiness) {
      return { first: "", middle: "", last: trimmedName };
    }

    // For individual names, split assuming "First Middle Last" format
    const parts = trimmedName.split(/\s+/).filter((p) => p.trim());

    if (parts.length === 0) {
      return { first: "", middle: "", last: "" };
    } else if (parts.length === 1) {
      // Only one part - assume it's the first name
      return { first: parts[0], middle: "", last: "" };
    } else if (parts.length === 2) {
      // Two parts: firstname + lastname
      return { first: parts[0], middle: "", last: parts[1] };
    } else {
      // Three or more parts: firstname + middlename(s) + lastname
      return {
        first: parts[0],
        middle: parts.slice(1, -1).join(" "),
        last: parts[parts.length - 1],
      };
    }
  };

  const refs = {
    oName: useRef(null),
    oFirst: useRef(null),
    oMiddle: useRef(null),
    oLast: useRef(null),
    oAddress: useRef(null),
    oCity: useRef(null),
    oZip: useRef(null),
    cFirst: useRef(null),
    cLast: useRef(null),
    cAddress: useRef(null),
    cCity: useRef(null),
    cZip: useRef(null),
    cEmail: useRef(null),
    oEmail: useRef(null),
    cDOB: useRef(null),
    cMobile: useRef(null),
    oMobile: useRef(null),
    cIdType: useRef(null),
  };

  const scrollToFirstError = (errors) => {
    const fields = Object.keys(errors);
    // Find the first field with an error and scroll to it
    for (const field of fields) {
      if (refs[field]?.current) {
        refs[field].current.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "nearest",
        });
        break;
      }
    }
  };

  // Wizard navigation functions
  const handleNext = useCallback(() => {
    // Validate notary selection for IPEN in "Notarization Method" step
    const currentStep = wizardSteps[currentWizardStep];
    if (currentStep?.id === "services") {
      // Check if all contractors have been viewed (only for St. Johns with multiple contractors)
      if (
        isStJohnsCounty() &&
        requiredContractors.length > 1 &&
        contractorsSeen.size < requiredContractors.length
      ) {
        setSubmitError(
          "Please navigate through all contractors using the arrow buttons",
        );
        return;
      }
      setCurrentContractorIndex(0);
      if (
        ownerSigning?.notarizationNeeded &&
        ownerNotarizationType === "ipen" &&
        hasOrgNotaries &&
        !ownerIpenNotaryId
      ) {
        // Check if owner needs IPEN notary selection
        setSubmitError(
          "Please select a notary for Property Owner IPEN notarization",
        );
        return;
      }
      // Check if contractor needs IPEN notary selection
      if (contractorSigning?.notarizationNeeded && hasOrgNotaries) {
        if (isStJohnsCounty()) {
          // Multi-contractor logic for St. Johns - check if any contractor needs IPEN and if shared notary is selected
          if (requiredContractors.length > 1) {
            // For multiple contractors, they all share the same notary
            // Check if any contractor has IPEN selected and if shared notary is set
            let hasIpenWithoutNotary = false;
            for (const contractor of requiredContractors) {
              const license = contractor?.licenseNumber;
              if (!license) continue;

              const settings = contractorSettings[license];
              if (settings?.notarizationType === "ipen") {
                if (!settings?.ipenNotaryId) {
                  hasIpenWithoutNotary = true;
                  break;
                }
              }
            }
            if (hasIpenWithoutNotary) {
              setSubmitError(
                "Please select a notary for Contractor IPEN notarization (applies to all contractors)",
              );
              return;
            }
          } else {
            // Single contractor in St. Johns
            for (const contractor of requiredContractors) {
              const license = contractor?.licenseNumber;
              if (!license) continue;

              const settings = contractorSettings[license];
              if (
                settings?.notarizationType === "ipen" &&
                !settings?.ipenNotaryId
              ) {
                setSubmitError(
                  `Please select a notary for ${contractor.businessName || contractor.dba_name || "Contractor"} IPEN notarization`,
                );
                return;
              }
            }
          }
        } else {
          // Single contractor logic for other counties
          if (
            contractorNotarizationType === "ipen" &&
            !contractorIpenNotaryId
          ) {
            setSubmitError(
              "Please select a notary for Contractor IPEN notarization",
            );
            return;
          }
        }
      }
    }

    // Validate notary selection for IPEN in old "notary" step (if it exists)
    if (
      currentStep?.id === "notary" &&
      notarizationType === "ipen" &&
      hasOrgNotaries &&
      !selectedNotary
    ) {
      setSubmitError("Please select a notary for IPEN notarization");
      return;
    }

    setSubmitError("");
    setCurrentWizardStep((prev) => Math.min(prev + 1, wizardSteps.length - 1));
  }, [
    wizardSteps,
    currentWizardStep,
    ownerSigning,
    contractorSigning,
    ownerNotarizationType,
    ownerIpenNotaryId,
    contractorNotarizationType,
    contractorIpenNotaryId,
    notarizationType,
    hasOrgNotaries,
    selectedNotary,
    contractorsSeen,
    requiredContractors,
    contractorSettings,
    isStJohnsCounty,
  ]);

  const handlePrevious = useCallback(() => {
    setCurrentWizardStep((prev) => Math.max(prev - 1, 0));
  }, []);

  const handleStepClick = useCallback(
    (stepIndex) => {
      if (stepIndex <= currentWizardStep) {
        setCurrentWizardStep(stepIndex);
      }
    },
    [currentWizardStep],
  );

  // Reset modal state
  const resetModalState = useCallback((formikRef) => {
    setCurrentWizardStep(0);
    setSelectedNotary(null);
    setAvailableNotaries([]);
    setSubmitError("");
    setIsAcknowledged(false);
    setNotarizationType("ron");
    setNotarySource("permitRockstar");
    setIpenPin("");
    setOwnerIpenNotaryId("");
    setContractorNotarizationType("ron");
    setContractorIpenNotaryId("");
    setContractorSettings({}); // Reset all contractor settings
    setConfirmedPricing(null);
    // Reset service selections
    setPlanReviewService({
      usePermitRockstar: false,
      useOrganization: false,
      customProvider: "",
    });
    setInspectionService({
      usePermitRockstar: false,
      useOrganization: false,
      customProvider: "",
      inspectionTypes: [],
    });
    // Reset loading states
    setLoadingNotaries(false);
    if (formikRef) {
      formikRef.resetForm();
    }
    // setContractorNotarizationType and setContractorIpenNotaryId are wrapper functions
    // that call stable state setters, so they're safe to omit from dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        oName: Yup.string(), // Keep for backward compatibility, but use individual fields for validation
        oFirst:
          ownerSigning?.notarizationNeeded || ownerSigning?.signingNeeded
            ? Yup.string().required("First name is required")
            : Yup.string(),
        oLast:
          ownerSigning?.notarizationNeeded || ownerSigning?.signingNeeded
            ? Yup.string().required("Last name is required")
            : Yup.string(),
        oAddress: ownerSigning?.notarizationNeeded
          ? Yup.string().required("Address is required")
          : Yup.string(),
        oCity: ownerSigning?.notarizationNeeded
          ? Yup.string().required("City is required")
          : Yup.string(),
        oZip: ownerSigning?.notarizationNeeded
          ? Yup.string()
              .required("Zip Code is required")
              .test("zipCode", "Invalid zip code", function (value) {
                return validator.isPostalCode(value, "US");
              })
          : Yup.string(),
        cFirst:
          contractorSigning?.notarizationNeeded ||
          contractorSigning?.signingNeeded
            ? Yup.string().required("First name is required")
            : Yup.string(),
        cLast:
          contractorSigning?.notarizationNeeded ||
          contractorSigning?.signingNeeded
            ? Yup.string().required("Last name is required")
            : Yup.string(),
        cAddress: contractorSigning?.notarizationNeeded
          ? Yup.string().required("Address is required")
          : Yup.string(),
        cCity: contractorSigning?.notarizationNeeded
          ? Yup.string().required("City is required")
          : Yup.string(),
        cZip: contractorSigning?.notarizationNeeded
          ? Yup.string()
              .required("Zip Code is required")
              .test("zipCode", "Invalid zip code", function (value) {
                return validator.isPostalCode(value, "US");
              })
          : Yup.string(),
        cEmail:
          contractorSigning?.notarizationNeeded ||
          contractorSigning?.signingNeeded
            ? Yup.string()
                .required("Email is required")
                .email("Must be a valid email")
            : Yup.string(),
        oEmail:
          ownerSigning?.notarizationNeeded || ownerSigning?.signingNeeded
            ? Yup.string()
                .required("Email is required")
                .email("Must be a valid email")
            : Yup.string(),

        cDOB: Yup.string(),
        cMobile:
          contractorSigning?.notarizationNeeded ||
          contractorSigning?.signingNeeded
            ? Yup.string()
                .required("Mobile number is required")
                .test(
                  "PhoneTest",
                  "Mobile number must be in format XXX-XXX-XXXX",
                  function (value) {
                    if (!value) return false;
                    // Check for exact format XXX-XXX-XXXX
                    const phoneRegex = /^\d{3}-\d{3}-\d{4}$/;
                    return phoneRegex.test(value);
                  },
                )
            : Yup.string(),
        oMobile:
          ownerSigning?.notarizationNeeded || ownerSigning?.signingNeeded
            ? Yup.string()
                .required("Mobile number is required")
                .test(
                  "PhoneTest",
                  "Mobile number must be in format XXX-XXX-XXXX",
                  function (value) {
                    if (!value) return false;
                    // Check for exact format XXX-XXX-XXXX
                    const phoneRegex = /^\d{3}-\d{3}-\d{4}$/;
                    return phoneRegex.test(value);
                  },
                )
            : Yup.string(),
        oMobileConsent:
          ownerSigning?.notarizationNeeded || ownerSigning?.signingNeeded
            ? Yup.boolean().oneOf(
                [true],
                "You must confirm that you have obtained the homeowner's permission to send text messages to the mobile phone number provided above.",
              )
            : Yup.boolean(),
        cIdType: Yup.string(),
      }),
    [ownerSigning, contractorSigning],
  );

  const commonOwnerName = selectedRow?.common_fields?.find(
    (cf) => cf.common_name === "ownerName",
  );

  // Split owner name into first, middle, last
  const commonOwnerEmail = selectedRow?.common_fields?.find(
    (cf) => cf.common_name === "ownerEmail",
  );
  const commonOwnerPhone = selectedRow?.common_fields?.find(
    (cf) => cf.common_name === "ownerPhone",
  );
  const ownerFullName =
    ownerSigning.notarizationNeeded || ownerSigning.signingNeeded
      ? commonOwnerName?.value ||
        selectedRow?.property_owner?.personalName ||
        ""
      : commonOwnerName?.value ||
        selectedRow?.property_owner?.personalName ||
        selectedRow?.property_owner?.name ||
        "";
  const ownerNameParts = splitFullName(ownerFullName);

  // Get current contractor data for form initialization
  const getCurrentContractorData = () => {
    if (isStJohnsCounty() && requiredContractors.length > 0) {
      return requiredContractors[currentContractorIndex] || {};
    }
    return selectedRow?.contractor || {};
  };

  const currentContractorData = getCurrentContractorData();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      cFirst: selectedRow?.contractor?.firstName || "",
      cMiddle: selectedRow?.contractor?.middleName || "",
      cLast: selectedRow?.contractor?.lastName || "",
      cAddress: selectedRow?.contractor?.address1 || "",
      cCity: selectedRow?.contractor?.city || "",
      cState: selectedRow?.contractor?.state || "",
      cZip: selectedRow?.contractor?.zip || "",
      cDOB: "",
      cEmail: selectedRow?.contractor?.email || "",
      cMobile: selectedRow?.contractor?.mobile || "",
      cIdType: selectedRow?.contractor?.idType || "",
      oFirst: ownerNameParts.first || "",
      oMiddle: ownerNameParts.middle || "",
      oLast: ownerNameParts.last || "",
      oName: ownerFullName || "",
      oEmail:
        commonOwnerEmail?.value || selectedRow?.property_owner?.email || "",
      oMobile:
        commonOwnerPhone?.value || selectedRow?.property_owner?.mobile || "",
      oMobileConsent: false,
    },
    validationSchema,

    onSubmit: async (values, helpers) => {
      try {
        setNotaryPaymentLoading(true);
        const payload = {
          ...values,
          oName: [values.oFirst, values.oMiddle, values.oLast]
            .filter(Boolean)
            .join(" "),
          cDOB: values.cDOB ? convertDayjsToObject(values.cDOB) : "",
          cBusinessName:
            currentContractorData?.businessName ||
            selectedRow?.contractor?.businessName,
          primaryAddress: selectedRow?.project_address,
          autoStartRecording: autoStartRecording,
          isAcknowledged: isAcknowledged,
          signingOrder: signingOrder,
          paymentCard: paymentCards[activeStep],
          saveCityZip: "",
          cLicense:
            currentContractorData?.licenseNumber ||
            selectedRow?.contractor?.licenseNumber,
          onlyOwner:
            !contractorSigning?.notarizationNeeded &&
            !contractorSigning?.signingNeeded,
          // IPEN & Notary Pool settings
          notarization_type: notarizationType,
          notary_source: notarySource,
          ipen_pin: ipenPin,
          owner_notarization_type: ownerNotarizationType,
          // For St. Johns: send contractorSettings object, for others: send single values
          contractor_notarization_type: contractorNotarizationType,
          owner_ipen_notary_id: ownerIpenNotaryId,
          contractor_ipen_notary_id: contractorIpenNotaryId,
          selected_contractors: contractorSettings,
          plan_review_service: planReviewService,
          inspection_service: inspectionService,
        };

        if (payload["onlyOwner"]) {
          payload["signingOrder"] = "owner";
        }

        if (currentUser?.user?.role !== "admin" && !currentUser?.user?.admin) {
          // Process payment only if not admin
          const resp = await axios.post(
            `${process.env.REACT_APP_BASE_URL}/nocApi/stripe/process-notary-payment`,
            {
              paymentCard: paymentCards[activeStep]?.id,
              task_id: selectedRow?.task_id,
              acknowledged: isAcknowledged,
              currentSession: currentSession,
              documents_type: docNotaryTypeValue,
            },
            { withCredentials: true },
          );

          // Store the confirmed pricing from backend
          if (resp.data.pricing) {
            setConfirmedPricing(resp.data.pricing);
          }

          const stripe = await loadStripe(process.env.REACT_APP_STRIPE);

          // Check if payment requires action (3D Secure, etc.)
          // If payment_status is already "succeeded", we can skip confirmation
          if (
            resp.data.payment_status === "succeeded" &&
            resp.data.valid === true
          ) {
            // Payment already succeeded, proceed directly
            const paymentIntent = {
              id: resp.data.payment_intent_id || resp.data.setup_intent_id,
              status: "succeeded",
            };
            const finalResp = await axios.post(
              `${process.env.REACT_APP_BASE_URL}/nocApi/stripe/finish-notary-payment`,
              {
                paymentIntent,
                task_id: selectedRow?.task_id,
                currentSession: currentSession,
                payload,
                documents_type: docNotaryTypeValue,
              },
              { withCredentials: true },
            );

            handlePostPayment(finalResp, helpers);
            return;
          }

          // Payment requires confirmation (3D Secure, etc.)
          // Stripe's confirmCardPayment/confirmCardSetup will automatically
          // show the 3D Secure modal if needed
          let result = null;

          try {
            if (resp.data.flow === "setup_intent") {
              result = await stripe.confirmCardSetup(resp.data.data);
            } else {
              result = await stripe.confirmCardPayment(resp.data.data);
            }

            if (result.error) {
              // Payment failed or was cancelled
              setSubmitError(
                result.error.message ||
                  "Payment failed. Please try again or use a different payment method.",
              );
              setNotaryPaymentLoading(false);
              return;
            }

            // Check the payment status after confirmation
            let status = null;
            let paymentIntent = null;

            if (resp.data.flow === "setup_intent") {
              status = result.setupIntent.status;
              paymentIntent = result.setupIntent;
            } else {
              status = result.paymentIntent.status;
              paymentIntent = result.paymentIntent;
            }

            // Only proceed if payment succeeded
            if (status === "succeeded") {
              const finalResp = await axios.post(
                `${process.env.REACT_APP_BASE_URL}/nocApi/stripe/finish-notary-payment`,
                {
                  paymentIntent,
                  task_id: selectedRow?.task_id,
                  currentSession: currentSession,
                  payload,
                  documents_type: docNotaryTypeValue,
                },
                { withCredentials: true },
              );

              handlePostPayment(finalResp, helpers);
            } else {
              // Payment not succeeded (shouldn't happen after confirmation, but handle it)
              setSubmitError(
                `Payment status: ${status}. Please try again or contact support.`,
              );
              setNotaryPaymentLoading(false);
            }
          } catch (error) {
            // Handle any errors during confirmation
            console.error("Payment confirmation error:", error);
            setSubmitError(
              error.message ||
                "An error occurred during payment confirmation. Please try again.",
            );
            setNotaryPaymentLoading(false);
          }
        } else {
          // If admin, skip payment but still call finish-notary-payment
          const finalResp = await axios.post(
            `${process.env.REACT_APP_BASE_URL}/nocApi/stripe/finish-notary-payment`,
            {
              task_id: selectedRow?.task_id,
              currentSession: currentSession,
              payload,
              documents_type: docNotaryTypeValue,
            },
            { withCredentials: true },
          );

          handlePostPayment(finalResp, helpers);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setNotaryPaymentLoading(false);
      }
    },
  });

  // Auto-fill owner information when ownerSigning becomes required (e.g., pp_ntbo with feeOwner)
  useEffect(() => {
    if (
      (ownerSigning?.notarizationNeeded || ownerSigning?.signingNeeded) &&
      selectedRow?.property_owner
    ) {
      // Only update if fields are empty to avoid overwriting user input
      // Prefill owner name fields if empty
      if (!formik.values.oFirst || !formik.values.oLast) {
        const ownerName =
          commonOwnerName?.value ||
          selectedRow.property_owner.personalName ||
          selectedRow.property_owner.name ||
          "";
        if (ownerName) {
          const nameParts = splitFullName(ownerName);
          if (!formik.values.oFirst) {
            formik.setFieldValue("oFirst", nameParts.first);
          }
          if (!formik.values.oMiddle) {
            formik.setFieldValue("oMiddle", nameParts.middle);
          }
          if (!formik.values.oLast) {
            formik.setFieldValue("oLast", nameParts.last);
          }
          // Also set oName for backward compatibility
          if (!formik.values.oName) {
            formik.setFieldValue("oName", ownerName);
          }
        }
      }
      // Prefill owner address fields if empty
      if (ownerSigning?.notarizationNeeded) {
        const addressParts = selectedRow.property_owner?.address_parts || {};
        if (!formik.values.oAddress && addressParts.address1) {
          formik.setFieldValue("oAddress", addressParts.address1);
        }
        if (!formik.values.oCity && addressParts.city) {
          formik.setFieldValue("oCity", addressParts.city);
        }
        if (!formik.values.oZip && addressParts.zip_code) {
          formik.setFieldValue("oZip", addressParts.zip_code);
        }
      }
      if (!formik.values.oEmail) {
        const ownerEmail =
          commonOwnerEmail?.value || selectedRow.property_owner.email || "";
        if (ownerEmail) {
          formik.setFieldValue("oEmail", ownerEmail);
        }
      }
      if (!formik.values.oMobile) {
        const ownerMobile =
          commonOwnerPhone?.value || selectedRow.property_owner.mobile || "";
        if (ownerMobile) {
          formik.setFieldValue("oMobile", ownerMobile);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ownerSigning?.notarizationNeeded,
    ownerSigning?.signingNeeded,
    selectedRow?.property_owner,
  ]);

  // Validation function for Signer Information step
  const isSignerInformationValid = useCallback(() => {
    // Check owner/agent fields (always shown)
    if (ownerSigning?.notarizationNeeded || ownerSigning?.signingNeeded) {
      if (
        !formik.values.oFirst ||
        !formik.values.oLast ||
        !formik.values.oEmail ||
        !formik.values.oMobile
      ) {
        return false;
      }
      // Check address fields if notarization is needed
      if (
        ownerSigning?.notarizationNeeded &&
        (!formik.values.oAddress || !formik.values.oCity || !formik.values.oZip)
      ) {
        return false;
      }
      // Check if mobile consent checkbox is checked
      if (!formik.values.oMobileConsent) {
        return false;
      }
    }

    // Check contractor fields (if contractor needs to sign/notarize)
    if (
      contractorSigning?.notarizationNeeded ||
      contractorSigning?.signingNeeded
    ) {
      if (!formik.values.cEmail || !formik.values.cMobile) {
        return false;
      }
      // If notarization needed, also check address fields
      if (contractorSigning?.notarizationNeeded) {
        if (
          !formik.values.cAddress ||
          !formik.values.cCity ||
          !formik.values.cZip
        ) {
          return false;
        }
      }
    }

    return true;
  }, [formik.values, ownerSigning, contractorSigning]);

  // Individual Step Components
  const renderOverviewStep = () => {
    return (
      <AnimatedStep>
        <Typography
          variant="h6"
          align="center"
          color="primary"
          fontWeight="bold"
          mb={2}
        >
          Document Overview & Requirements
        </Typography>

        <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
          <Typography variant="body2">{getInstructionText()}</Typography>
        </Alert>

        {/* Project Info */}
        <Card sx={{ mb: 2, p: 2, bgcolor: "primary.main", color: "white" }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography
                variant="caption"
                sx={{ opacity: 0.9, fontSize: "0.7rem" }}
              >
                PROJECT NAME
              </Typography>
              <Typography variant="body2" fontWeight="600" sx={{ mt: 0.5 }}>
                {selectedRow?.package_name || "N/A"}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography
                variant="caption"
                sx={{ opacity: 0.9, fontSize: "0.7rem" }}
              >
                JOB LOCATION
              </Typography>
              <Typography variant="body2" fontWeight="600" sx={{ mt: 0.5 }}>
                {selectedRow?.project_address || "N/A"}
              </Typography>
            </Grid>
          </Grid>
        </Card>

        {/* Documents Requirements Table */}
        <Card sx={{ mb: 2 }}>
          <Box sx={{ p: 1.5, pb: 1 }}>
            <Typography
              variant="subtitle2"
              fontWeight="bold"
              color="primary"
              gutterBottom
            >
              📋 Selected Documents & Requirements
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              Review which documents require signatures and notarization from
              each party
            </Typography>
          </Box>
          <TableContainer sx={{ maxHeight: 350 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      bgcolor: "primary.main",
                      color: "white",
                      py: 1,
                    }}
                  >
                    Document
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      bgcolor: "primary.main",
                      color: "white",
                      py: 1,
                    }}
                    align="center"
                  >
                    Owner
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      bgcolor: "primary.main",
                      color: "white",
                      py: 1,
                    }}
                    align="center"
                  >
                    Contractor
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      bgcolor: "primary.main",
                      color: "white",
                      py: 1,
                    }}
                    align="center"
                  >
                    Method
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deferredFilteredDocs.map((doc, index) => {
                  // Special handling for pp_ntbo - use signerType to determine who signs
                  const ppNtboSignerType =
                    doc.name === "pp_ntbo"
                      ? selectedRow?.documents?.pp_ntbo?.sections?.[1]?.fields
                          ?.signerType
                      : null;

                  let ownerSign, ownerNotary, contractorSign, contractorNotary;

                  if (doc.name === "pp_ntbo" && ppNtboSignerType) {
                    // Override signatures based on signerType
                    if (ppNtboSignerType === "feeOwner") {
                      ownerSign = true;
                      ownerNotary = false;
                      contractorSign = false;
                      contractorNotary = false;
                    } else if (ppNtboSignerType === "feeContractor") {
                      ownerSign = false;
                      ownerNotary = false;
                      contractorSign = true;
                      contractorNotary = false;
                    } else {
                      // Fallback to original signatures if signerType is invalid
                      ownerSign = doc.signatures?.owner?.sign;
                      ownerNotary = doc.signatures?.owner?.notary;
                      contractorSign = doc.signatures?.contractor?.sign;
                      contractorNotary = doc.signatures?.contractor?.notary;
                    }
                  } else {
                    // Normal handling for other documents
                    ownerSign = doc.signatures?.owner?.sign;
                    ownerNotary = doc.signatures?.owner?.notary;
                    contractorSign = doc.signatures?.contractor?.sign;
                    contractorNotary = doc.signatures?.contractor?.notary;
                  }

                  return (
                    <TableRow key={index} hover>
                      <TableCell sx={{ py: 1 }}>
                        <Typography variant="body2" fontWeight="medium">
                          {getDocumentDisplayName(doc.name)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ py: 1 }}>
                        {ownerNotary ? (
                          <Chip
                            label="Notarize"
                            size="small"
                            color="secondary"
                            sx={{ fontSize: "0.7rem", height: 20 }}
                          />
                        ) : ownerSign ? (
                          <Chip
                            label="Sign"
                            size="small"
                            color="info"
                            sx={{ fontSize: "0.7rem", height: 20 }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ py: 1 }}>
                        {contractorNotary ? (
                          <Chip
                            label="Notarize"
                            size="small"
                            color="secondary"
                            sx={{ fontSize: "0.7rem", height: 20 }}
                          />
                        ) : contractorSign ? (
                          <Chip
                            label="Sign"
                            size="small"
                            color="info"
                            sx={{ fontSize: "0.7rem", height: 20 }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ py: 1 }}>
                        {ownerNotary || contractorNotary ? (
                          <Chip
                            label="RON/IPEN"
                            size="small"
                            color="success"
                            variant="outlined"
                            sx={{ fontSize: "0.7rem", height: 20 }}
                          />
                        ) : ownerSign || contractorSign ? (
                          <Chip
                            label="E-Sign"
                            size="small"
                            color="info"
                            variant="outlined"
                            sx={{ fontSize: "0.7rem", height: 20 }}
                          />
                        ) : (
                          <Chip
                            label="No Action"
                            size="small"
                            color="default"
                            variant="outlined"
                            sx={{ fontSize: "0.7rem", height: 20 }}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {selectedRow?.assigned_package?.need_assign && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              {signingOrder === "contractor"
                ? "If the contractor is selected as the first signer, the homeowner cannot begin their part of the signing process until the contractor completes theirs."
                : ""}{" "}
              The documents will be automatically forwarded to the contractor as
              soon as the process starts.
            </Typography>
          </Alert>
        )}
      </AnimatedStep>
    );
  };

  const renderMethodStep = () => (
    <AnimatedStep>
      <Typography
        variant="h6"
        align="center"
        color="primary"
        fontWeight="bold"
        mb={2}
      >
        Notarization Method
      </Typography>

      {contractorSigning?.notarizationNeeded ||
      ownerSigning?.notarizationNeeded ? (
        <Stack spacing={3}>
          <FormControl
            component="fieldset"
            disabled={notaryPaymentLoading}
            sx={{ width: "100%" }}
          >
            <RadioGroup
              value={notarizationType}
              onChange={(e) => setNotarizationType(e.target.value)}
              sx={{ gap: 2 }}
            >
              {/* RON Option */}
              <StepCard active={notarizationType === "ron"}>
                <CardContent>
                  <FormControlLabel
                    value="ron"
                    control={<Radio />}
                    label={
                      <Box sx={{ ml: 1 }}>
                        <Typography variant="h6" fontWeight="bold">
                          Remote Online Notarization (RON)
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Notarization completed online via video call
                        </Typography>
                      </Box>
                    }
                    sx={{ width: "100%", alignItems: "flex-start" }}
                  />

                  {/* RON Notary Source Selection */}
                  {notarizationType === "ron" && hasOrgNotaries && (
                    <Box sx={{ ml: 6, mt: 2 }}>
                      <FormLabel
                        component="legend"
                        sx={{
                          fontSize: "0.9rem",
                          mb: 1,
                          color: "text.primary",
                        }}
                      >
                        Select Notary Provider
                      </FormLabel>
                      <RadioGroup
                        value={notarySource}
                        onChange={(e) => setNotarySource(e.target.value)}
                      >
                        <FormControlLabel
                          value="permitRockstar"
                          control={<Radio size="small" />}
                          label="Use Permit Rockstar notary"
                        />
                        <FormControlLabel
                          value="organization"
                          control={<Radio size="small" />}
                          label="Use my organization's notary"
                          disabled={!hasOrgNotaries}
                        />
                      </RadioGroup>
                    </Box>
                  )}
                </CardContent>
              </StepCard>

              {/* IPEN Option */}
              <StepCard active={notarizationType === "ipen"}>
                <CardContent>
                  <FormControlLabel
                    control={<Radio />}
                    disabled={!canUseIpen}
                    label={
                      <Box sx={{ ml: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 0.5,
                          }}
                        >
                          <Typography
                            variant="h6"
                            fontWeight="bold"
                            component="span"
                          >
                            In-Person Electronic Notarization (IPEN)
                          </Typography>
                          {!hasOrgNotaries && (
                            <Chip
                              label="Requires org notary"
                              size="small"
                              color="warning"
                            />
                          )}
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          Notarization completed in person with your
                          organization's notary
                        </Typography>
                      </Box>
                    }
                    sx={{ width: "100%", alignItems: "flex-start" }}
                  />
                </CardContent>
              </StepCard>
            </RadioGroup>
          </FormControl>

          {/* IPEN PIN Display */}
          {notarizationType === "ipen" && ipenPin && (
            <Card
              sx={{ p: 2, bgcolor: "#e3f2fd", border: "2px solid #2196f3" }}
            >
              <Typography
                variant="subtitle1"
                color="primary"
                fontWeight="bold"
                align="center"
                mb={1}
              >
                📌 Verification PIN
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  fontFamily: "monospace",
                  letterSpacing: 4,
                  textAlign: "center",
                  color: "#1976d2",
                  fontWeight: "bold",
                  mb: 1,
                }}
              >
                {ipenPin}
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center">
                Provide this PIN to your notary to verify physical presence
              </Typography>
            </Card>
          )}

          {/* Mixed Session Warning */}
          {notarizationType === "ipen" &&
            ownerSigning.notarizationNeeded &&
            contractorSigning.notarizationNeeded && (
              <Alert severity="info">
                <Typography variant="body2">
                  ℹ️ <strong>Note:</strong> Since both owner and contractor
                  notarization is required, contractor documents will use IPEN
                  while owner documents will be processed separately via RON.
                </Typography>
              </Alert>
            )}

          {/* IPEN Not Available Message */}
          {!canUseIpen && !hasOrgNotaries && (
            <Alert severity="warning">
              <Typography variant="body2">
                💡 To enable IPEN, add notary-certified users to your
                organization
              </Typography>
            </Alert>
          )}
        </Stack>
      ) : (
        <Card sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="h6" color="text.secondary">
            No notarization required for these documents.
          </Typography>
        </Card>
      )}
    </AnimatedStep>
  );

  const renderNotaryStep = () => (
    <AnimatedStep>
      <Typography
        variant="h6"
        align="center"
        color="primary"
        fontWeight="bold"
        mb={2}
      >
        Notary Selection
      </Typography>

      {notarizationType === "ipen" && hasOrgNotaries ? (
        <Card
          sx={{
            p: 3,
            background: "linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%)",
            border: "1px solid #e3f2fd",
            borderRadius: "16px",
          }}
        >
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Typography variant="h6" color="primary" fontWeight="bold" mb={1}>
              🏢 Organization Notary Selection
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Select a certified notary from your organization for in-person
              notarization
            </Typography>
          </Box>

          {loadingNotaries ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                🔍 Loading organization notaries...
              </Typography>
            </Box>
          ) : availableNotaries.length > 0 ? (
            <RadioGroup
              value={selectedNotary?.notary_id || ""}
              onChange={(e) => {
                const notary = availableNotaries.find(
                  (n) => n.notary_id === e.target.value,
                );
                setSelectedNotary(notary);
              }}
              sx={{ gap: 2 }}
            >
              {availableNotaries.map((notary) => (
                <StepCard
                  key={notary.notary_id}
                  active={selectedNotary?.notary_id === notary.notary_id}
                  sx={{
                    borderRadius: "12px",
                    border:
                      selectedNotary?.notary_id === notary.notary_id
                        ? "2px solid #1976d2"
                        : "2px solid #e0e0e0",
                    background:
                      selectedNotary?.notary_id === notary.notary_id
                        ? "linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)"
                        : "white",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 8px 25px rgba(25, 118, 210, 0.15)",
                    },
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <FormControlLabel
                      value={notary.notary_id}
                      control={
                        <Radio
                          sx={{
                            color:
                              selectedNotary?.notary_id === notary.notary_id
                                ? "#1976d2"
                                : "#9e9e9e",
                            "&.Mui-checked": {
                              color: "#1976d2",
                            },
                          }}
                        />
                      }
                      label={
                        <Box
                          sx={{
                            ml: 1,
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: "50%",
                              background:
                                "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "white",
                              fontWeight: "bold",
                              fontSize: "1.2rem",
                            }}
                          >
                            {notary.firstName?.[0]}
                            {notary.lastName?.[0]}
                          </Box>
                          <Box>
                            <Typography
                              variant="h6"
                              fontWeight="bold"
                              color="primary"
                            >
                              {notary.firstName} {notary.lastName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {notary.email}
                            </Typography>
                            <Chip
                              label="Certified Notary"
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ mt: 0.5, fontSize: "0.7rem" }}
                            />
                          </Box>
                        </Box>
                      }
                      sx={{ width: "100%", alignItems: "flex-start", m: 0 }}
                    />
                  </CardContent>
                </StepCard>
              ))}
            </RadioGroup>
          ) : (
            <Alert
              severity="warning"
              sx={{
                borderRadius: "12px",
                background: "linear-gradient(135deg, #fff3e0 0%, #ffffff 100%)",
                border: "1px solid #ffb74d",
              }}
            >
              <Typography variant="body2" fontWeight="medium">
                ⚠️ No available notaries found in your organization. Please
                contact your administrator to add certified notaries.
              </Typography>
            </Alert>
          )}
        </Card>
      ) : notarizationType === "ipen" && !hasOrgNotaries ? (
        <Card
          sx={{
            p: 3,
            textAlign: "center",
            background: "linear-gradient(135deg, #fff3e0 0%, #ffffff 100%)",
            border: "1px solid #ffb74d",
            borderRadius: "16px",
          }}
        >
          <Typography
            variant="h6"
            color="warning.main"
            fontWeight="bold"
            mb={2}
          >
            🚫 IPEN Not Available
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Your organization doesn't have certified notaries available for
            in-person notarization.
          </Typography>
          <Alert severity="info" sx={{ borderRadius: "12px" }}>
            <Typography variant="body2">
              💡 To enable IPEN, your organization needs to have certified
              notaries. Please contact your administrator or switch to Remote
              Online Notarization (RON).
            </Typography>
          </Alert>
        </Card>
      ) : (
        <Card
          sx={{
            p: 4,
            textAlign: "center",
            background: "linear-gradient(135deg, #e8f5e8 0%, #ffffff 100%)",
            border: "1px solid #4caf50",
            borderRadius: "16px",
          }}
        >
          <Typography
            variant="h6"
            color="success.main"
            fontWeight="bold"
            mb={2}
          >
            ✅ Notary Assignment Complete
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {notarizationType === "ron"
              ? "Your notary will be automatically assigned after payment and scheduling."
              : "No notary selection required for this document type."}
          </Typography>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: 1,
              borderRadius: "20px",
              background: "linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)",
              color: "white",
            }}
          >
            <Typography variant="body2" fontWeight="bold">
              🎯 Ready to Continue
            </Typography>
          </Box>
        </Card>
      )}
    </AnimatedStep>
  );

  const renderServicesStep = () => (
    <AnimatedStep>
      <Typography
        variant="h6"
        align="center"
        color="primary"
        fontWeight="bold"
        mb={1}
      >
        Notarization Method
      </Typography>

      <Typography variant="body2" color="text.secondary" align="center" mb={3}>
        Select notarization method for each party
      </Typography>

      <Grid container spacing={2} sx={{ my: 2 }}>
        {/* Contractor Notarization */}
        {contractorSigning?.notarizationNeeded && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              sx={{
                p: 2.5,
                height: "100%",
                background: "linear-gradient(135deg, #f0f8ff 0%, #ffffff 100%)",
                border: "1px solid #e1f5fe",
                borderRadius: "12px",
                boxShadow: "0 2px 12px rgba(0, 150, 136, 0.08)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    flex: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "10px",
                      background:
                        "linear-gradient(135deg, #009688 0%, #26a69a 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontSize: "1.2rem",
                    }}
                  >
                    🔧
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      color="primary"
                    >
                      Contractor
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {isStJohnsCounty() && requiredContractors.length > 0
                        ? requiredContractors[currentContractorIndex]
                            ?.businessName ||
                          requiredContractors[currentContractorIndex]
                            ?.dba_name ||
                          "Choose notarization method"
                        : selectedRow?.contractor?.businessName ||
                          selectedRow?.contractor?.dba_name ||
                          "Choose notarization method"}
                    </Typography>
                  </Box>
                </Box>
                {isStJohnsCounty() && requiredContractors.length > 1 && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        // Save current contractor's form values before switching (only for Signer Information step)

                        const prevIndex =
                          currentContractorIndex > 0
                            ? currentContractorIndex - 1
                            : requiredContractors.length - 1;
                        // Mark previous contractor as seen when navigating back
                        setContractorsSeen((prev) => {
                          const newSet = new Set(prev);
                          newSet.add(prevIndex);
                          return newSet;
                        });
                        setCurrentContractorIndex(prevIndex);
                      }}
                      sx={{
                        color: "primary.main",
                        "&:hover": {
                          backgroundColor: "rgba(0, 150, 136, 0.1)",
                        },
                      }}
                    >
                      <ArrowBackIcon />
                    </IconButton>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ minWidth: "40px", textAlign: "center" }}
                    >
                      {currentContractorIndex + 1} /{" "}
                      {requiredContractors.length}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => {
                        const nextIndex =
                          currentContractorIndex <
                          requiredContractors.length - 1
                            ? currentContractorIndex + 1
                            : 0;
                        // Mark current and next contractor as seen
                        setContractorsSeen((prev) => {
                          const newSet = new Set(prev);
                          newSet.add(currentContractorIndex);
                          newSet.add(nextIndex);
                          // Clear error if all contractors are now seen
                          if (newSet.size >= requiredContractors.length) {
                            setSubmitError("");
                          }
                          return newSet;
                        });
                        setCurrentContractorIndex(nextIndex);
                      }}
                      sx={{
                        color:
                          submitError &&
                          requiredContractors.length > 1 &&
                          contractorsSeen.size < requiredContractors.length
                            ? "error.main"
                            : "primary.main",
                        "&:hover": {
                          backgroundColor:
                            submitError &&
                            requiredContractors.length > 1 &&
                            contractorsSeen.size < requiredContractors.length
                              ? "rgba(211, 47, 47, 0.1)"
                              : "rgba(0, 150, 136, 0.1)",
                        },
                      }}
                    >
                      <ArrowForwardIcon />
                    </IconButton>
                  </Box>
                )}
              </Box>

              <FormControl component="fieldset" sx={{ width: "100%" }}>
                <RadioGroup
                  value={getContractorNotarizationType()}
                  onChange={(e) =>
                    setContractorNotarizationType(e.target.value)
                  }
                  sx={{ gap: 1 }}
                >
                  <FormControlLabel
                    value="ron"
                    control={<Radio size="small" />}
                    label={
                      <Box sx={{ py: 0.5 }}>
                        <Typography variant="body2" fontWeight="medium">
                          RON - Remote Online Notarization
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Notarization completed online via video call
                        </Typography>
                      </Box>
                    }
                    sx={{
                      p: 1,
                      borderRadius: "8px",
                      border:
                        getContractorNotarizationType() === "ron"
                          ? "2px solid #009688"
                          : "1px solid #e0e0e0",
                      background:
                        getContractorNotarizationType() === "ron"
                          ? "rgba(0, 150, 136, 0.08)"
                          : "transparent",
                      transition: "all 0.2s ease",
                      m: 0,
                      mb: 1,
                      "&:hover": {
                        background: "rgba(0, 150, 136, 0.04)",
                      },
                    }}
                  />
                  <FormControlLabel
                    value="ipen"
                    control={<Radio size="small" disabled={!canUseIpen} />}
                    label={
                      <Box sx={{ py: 0.5 }}>
                        <Typography variant="body2" fontWeight="medium">
                          IPEN - In-Person Electronic Notarization
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Notarization completed in person with your
                          organization's notary
                        </Typography>
                      </Box>
                    }
                    sx={{
                      p: 1,
                      borderRadius: "8px",
                      border:
                        getContractorNotarizationType() === "ipen"
                          ? "2px solid #009688"
                          : "1px solid #e0e0e0",
                      background:
                        getContractorNotarizationType() === "ipen"
                          ? "rgba(0, 150, 136, 0.08)"
                          : "transparent",
                      transition: "all 0.2s ease",
                      m: 0,
                      opacity: !canUseIpen ? 0.5 : 1,
                      "&:hover": {
                        background: "rgba(0, 150, 136, 0.04)",
                      },
                    }}
                  />
                </RadioGroup>
              </FormControl>

              {/* IPEN Notary Selection for Contractor */}
              {getContractorNotarizationType() === "ipen" && hasOrgNotaries && (
                <Box sx={{ mt: 2, pl: 4 }}>
                  <FormControl
                    fullWidth
                    size="small"
                    variant="filled"
                    error={
                      getContractorNotarizationType() === "ipen" &&
                      hasOrgNotaries &&
                      !getContractorIpenNotaryId() &&
                      wizardSteps[currentWizardStep]?.id === "services"
                    }
                    required
                  >
                    <InputLabel id="contractor-ipen-notary-select-label">
                      Select Notary
                    </InputLabel>
                    <Select
                      labelId="contractor-ipen-notary-select-label"
                      id="contractor-ipen-notary-select"
                      value={getContractorIpenNotaryId()}
                      label="Select Notary"
                      onChange={(e) =>
                        setContractorIpenNotaryId(e.target.value)
                      }
                      disabled={
                        notaryPaymentLoading ||
                        (isStJohnsCounty() &&
                          requiredContractors.length > 1 &&
                          currentContractorIndex !== 0 &&
                          getContractorIpenNotaryId() && // Only disable if notary is already selected
                          // Check if any other contractor has IPEN selected
                          // If no other contractor has IPEN, allow current contractor to select notary
                          requiredContractors.some(
                            (contractor, index) =>
                              index !== currentContractorIndex &&
                              contractorSettings[contractor?.licenseNumber]
                                ?.notarizationType === "ipen",
                          ))
                      }
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 200,
                          },
                        },
                      }}
                    >
                      {currentUser?.tier?.notary?.subscriptions
                        ?.filter(
                          (sub) =>
                            sub.is_subuser === false || sub.is_subuser === true,
                        )
                        .map((subscription) => (
                          <MenuItem
                            key={subscription.user_id}
                            value={subscription.user_id}
                          >
                            {subscription.full_name}
                          </MenuItem>
                        ))}
                    </Select>
                    {getContractorNotarizationType() === "ipen" &&
                      hasOrgNotaries &&
                      !getContractorIpenNotaryId() &&
                      wizardSteps[currentWizardStep]?.id === "services" && (
                        <FormHelperText>
                          Please select a notary for IPEN notarization
                        </FormHelperText>
                      )}
                  </FormControl>
                </Box>
              )}
            </Card>
          </Grid>
        )}

        {/* Property Owner Notarization */}
        {ownerSigning?.notarizationNeeded && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              sx={{
                p: 2.5,
                height: "100%",
                background: "linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%)",
                border: "1px solid #e3f2fd",
                borderRadius: "12px",
                boxShadow: "0 2px 12px rgba(25, 118, 210, 0.08)",
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "10px",
                    background:
                      "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontSize: "1.2rem",
                  }}
                >
                  🏠
                </Box>
                <Box>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    color="primary"
                  >
                    Property Owner
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Choose notarization method
                  </Typography>
                </Box>
              </Box>

              <FormControl component="fieldset" sx={{ width: "100%" }}>
                <RadioGroup
                  value={ownerNotarizationType}
                  onChange={(e) => setOwnerNotarizationType(e.target.value)}
                  sx={{ gap: 1 }}
                >
                  <FormControlLabel
                    value="ron"
                    control={<Radio size="small" />}
                    label={
                      <Box sx={{ py: 0.5 }}>
                        <Typography variant="body2" fontWeight="medium">
                          RON - Remote Online Notarization
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Notarization completed online via video call
                        </Typography>
                      </Box>
                    }
                    sx={{
                      p: 1,
                      borderRadius: "8px",
                      border:
                        ownerNotarizationType === "ron"
                          ? "2px solid #1976d2"
                          : "1px solid #e0e0e0",
                      background:
                        ownerNotarizationType === "ron"
                          ? "rgba(25, 118, 210, 0.08)"
                          : "transparent",
                      transition: "all 0.2s ease",
                      m: 0,
                      mb: 1,
                      "&:hover": {
                        background: "rgba(25, 118, 210, 0.04)",
                      },
                    }}
                  />
                  <FormControlLabel
                    value="ipen"
                    control={<Radio size="small" disabled={!hasOrgNotaries} />}
                    label={
                      <Box sx={{ py: 0.5 }}>
                        <Typography variant="body2" fontWeight="medium">
                          IPEN - In-Person Electronic Notarization
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Notarization completed in person with your
                          organization's notary
                        </Typography>
                      </Box>
                    }
                    sx={{
                      p: 1,
                      borderRadius: "8px",
                      border:
                        ownerNotarizationType === "ipen"
                          ? "2px solid #1976d2"
                          : "1px solid #e0e0e0",
                      background:
                        ownerNotarizationType === "ipen"
                          ? "rgba(25, 118, 210, 0.08)"
                          : "transparent",
                      transition: "all 0.2s ease",
                      m: 0,
                      opacity: !hasOrgNotaries ? 0.5 : 1,
                      "&:hover": {
                        background: "rgba(25, 118, 210, 0.04)",
                      },
                    }}
                  />
                </RadioGroup>
              </FormControl>

              {/* IPEN Notary Selection for Owner */}
              {ownerNotarizationType === "ipen" && hasOrgNotaries && (
                <Box sx={{ mt: 2, pl: 4 }}>
                  <FormControl
                    fullWidth
                    size="small"
                    variant="filled"
                    error={
                      ownerNotarizationType === "ipen" &&
                      hasOrgNotaries &&
                      !ownerIpenNotaryId &&
                      wizardSteps[currentWizardStep]?.id === "services"
                    }
                    required
                  >
                    <InputLabel id="owner-ipen-notary-select-label">
                      Select Notary
                    </InputLabel>
                    <Select
                      labelId="owner-ipen-notary-select-label"
                      id="owner-ipen-notary-select"
                      value={ownerIpenNotaryId}
                      label="Select Notary"
                      onChange={(e) => setOwnerIpenNotaryId(e.target.value)}
                      disabled={notaryPaymentLoading}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 200,
                          },
                        },
                      }}
                    >
                      {currentUser?.tier?.notary?.subscriptions
                        ?.filter(
                          (sub) =>
                            sub.is_subuser === false || sub.is_subuser === true,
                        )
                        .map((subscription) => (
                          <MenuItem
                            key={subscription.user_id}
                            value={subscription.user_id}
                          >
                            {subscription.full_name}
                          </MenuItem>
                        ))}
                    </Select>
                    {ownerNotarizationType === "ipen" &&
                      hasOrgNotaries &&
                      !ownerIpenNotaryId &&
                      wizardSteps[currentWizardStep]?.id === "services" && (
                        <FormHelperText>
                          Please select a notary for IPEN notarization
                        </FormHelperText>
                      )}
                  </FormControl>
                </Box>
              )}

              {!hasOrgNotaries && (
                <Alert severity="warning" sx={{ mt: 2, borderRadius: "8px" }}>
                  <Typography variant="body2">
                    IPEN requires organization notaries. Permit Rockstar
                    notaries cannot perform IPEN notarization. Please add
                    certified notaries to your organization to enable this
                    option.
                  </Typography>
                </Alert>
              )}
            </Card>
          </Grid>
        )}
      </Grid>
    </AnimatedStep>
  );

  const renderInformationStep = () => (
    <AnimatedStep>
      <Typography
        variant="h6"
        align="center"
        color="primary"
        fontWeight="bold"
        mb={0}
      >
        Signer Information
      </Typography>

      <Typography align="center" color="text.secondary" variant="body2" mb={2}>
        {!contractorSigning?.notarizationNeeded &&
        !contractorSigning?.signingNeeded
          ? "Overview of the signer"
          : "Overview of the signers"}
      </Typography>

      <Grid container spacing={3}>
        {contractorSigning?.notarizationNeeded ||
        contractorSigning?.signingNeeded ? (
          <Grid size={{ sm: 12, md: 6 }}>
            <Card
              sx={{
                p: 3,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                background:
                  signingOrder === "contractor"
                    ? "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)"
                    : "linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%)",
                borderRadius: "20px",
                border: "1px solid rgba(25, 118, 210, 0.1)",
                boxShadow:
                  "0 8px 24px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(25, 118, 210, 0.05) inset",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    signingOrder === "contractor"
                      ? "linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(25, 118, 210, 0.05) 100%)"
                      : "linear-gradient(135deg, rgba(25, 118, 210, 0.03) 0%, rgba(25, 118, 210, 0) 100%)",
                  pointerEvents: "none",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                },
                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  boxShadow:
                    "0 12px 32px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(25, 118, 210, 0.1) inset",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 3,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    flex: 1,
                  }}
                >
                  <Typography
                    fontWeight="bold"
                    variant="h5"
                    sx={{
                      color: "primary.main",
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    Contractor
                  </Typography>
                  {/* <Typography variant="caption" color="text.secondary" sx={{ position: "relative", zIndex: 1 }}>
                    {isStJohnsCounty() && requiredContractors.length > 0
                      ? requiredContractors[currentContractorIndex]?.businessName ||
                        requiredContractors[currentContractorIndex]?.dba_name ||
                        ""
                      : selectedRow?.contractor?.businessName ||
                        selectedRow?.contractor?.dba_name ||
                        ""}
                  </Typography> */}
                </Box>
                {isStJohnsCounty() && requiredContractors.length > 1 && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        const prevIndex =
                          currentContractorIndex > 0
                            ? currentContractorIndex - 1
                            : requiredContractors.length - 1;
                        setContractorsSeen((prev) => {
                          const newSet = new Set(prev);
                          newSet.add(prevIndex);
                          return newSet;
                        });
                        setCurrentContractorIndex(prevIndex);
                        setSubmitError(""); // Clear error on navigation
                      }}
                      sx={{
                        color: "primary.main",
                        "&:hover": {
                          backgroundColor: "rgba(0, 150, 136, 0.1)",
                        },
                      }}
                    >
                      <ArrowBackIcon />
                    </IconButton>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ minWidth: "40px", textAlign: "center" }}
                    >
                      {currentContractorIndex + 1} /{" "}
                      {requiredContractors.length}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => {
                        const nextIndex =
                          currentContractorIndex <
                          requiredContractors.length - 1
                            ? currentContractorIndex + 1
                            : 0;
                        setContractorsSeen((prev) => {
                          const newSet = new Set(prev);
                          newSet.add(currentContractorIndex);
                          newSet.add(nextIndex);
                          // Clear error if all contractors are now seen
                          if (newSet.size >= requiredContractors.length) {
                            setSubmitError("");
                          }
                          return newSet;
                        });
                        setCurrentContractorIndex(nextIndex);
                      }}
                      sx={{
                        color:
                          submitError &&
                          requiredContractors.length > 1 &&
                          contractorsSeen.size < requiredContractors.length
                            ? "error.main"
                            : "primary.main",
                        "&:hover": {
                          backgroundColor:
                            submitError &&
                            requiredContractors.length > 1 &&
                            contractorsSeen.size < requiredContractors.length
                              ? "rgba(211, 47, 47, 0.1)"
                              : "rgba(0, 150, 136, 0.1)",
                        },
                      }}
                    >
                      <ArrowForwardIcon />
                    </IconButton>
                  </Box>
                )}
              </Box>

              <Stack
                spacing={2}
                sx={{ position: "relative", zIndex: 1, flex: 1 }}
              >
                <Box sx={{ display: "flex", gap: 1 }}>
                  <TextField
                    ref={refs.cFirst}
                    disabled
                    size="small"
                    error={!!(formik.touched.cFirst && formik.errors.cFirst)}
                    fullWidth
                    helperText={formik.touched.cFirst && formik.errors.cFirst}
                    name="cFirst"
                    value={formik.values.cFirst}
                    label="First Name"
                    variant="filled"
                    sx={{
                      "& .MuiFilledInput-root": {
                        backgroundColor: "rgba(255, 255, 255, 1)",
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 1)",
                        },
                      },
                    }}
                  />
                  <TextField
                    disabled
                    size="small"
                    error={!!(formik.touched.cMiddle && formik.errors.cMiddle)}
                    fullWidth
                    helperText={formik.touched.cMiddle && formik.errors.cMiddle}
                    name="cMiddle"
                    value={formik.values.cMiddle}
                    label="Middle Name"
                    variant="filled"
                    sx={{
                      "& .MuiFilledInput-root": {
                        backgroundColor: "rgba(255, 255, 255, 1)",
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 1)",
                        },
                      },
                    }}
                  />
                  <TextField
                    ref={refs.cLast}
                    disabled
                    size="small"
                    error={!!(formik.touched.cLast && formik.errors.cLast)}
                    fullWidth
                    helperText={formik.touched.cLast && formik.errors.cLast}
                    name="cLast"
                    value={formik.values.cLast}
                    label="Last Name"
                    variant="filled"
                    sx={{
                      "& .MuiFilledInput-root": {
                        backgroundColor: "rgba(255, 255, 255, 1)",
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 1)",
                        },
                      },
                    }}
                  />
                </Box>

                {contractorSigning?.notarizationNeeded && (
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <TextField
                      ref={refs.cAddress}
                      disabled={notaryPaymentLoading}
                      size="small"
                      error={
                        !!(formik.touched.cAddress && formik.errors.cAddress)
                      }
                      fullWidth
                      helperText={
                        formik.touched.cAddress && formik.errors.cAddress
                      }
                      name="cAddress"
                      value={formik.values.cAddress}
                      onChange={formik.handleChange}
                      label="Address"
                      variant="filled"
                      sx={{
                        "& .MuiFilledInput-root": {
                          backgroundColor: "rgba(255, 255, 255, 0.95)",
                          "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 1)",
                          },
                        },
                      }}
                    />
                    <TextField
                      ref={refs.cCity}
                      disabled={notaryPaymentLoading}
                      size="small"
                      error={!!(formik.touched.cCity && formik.errors.cCity)}
                      fullWidth
                      helperText={formik.touched.cCity && formik.errors.cCity}
                      name="cCity"
                      value={formik.values.cCity}
                      onChange={formik.handleChange}
                      label="City"
                      variant="filled"
                      sx={{
                        "& .MuiFilledInput-root": {
                          backgroundColor: "rgba(255, 255, 255, 0.95)",
                          "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 1)",
                          },
                        },
                      }}
                    />
                    <TextField
                      ref={refs.cZip}
                      disabled={notaryPaymentLoading}
                      size="small"
                      error={!!(formik.touched.cZip && formik.errors.cZip)}
                      fullWidth
                      helperText={formik.touched.cZip && formik.errors.cZip}
                      name="cZip"
                      value={formik.values.cZip}
                      onChange={formik.handleChange}
                      label="ZIP"
                      variant="filled"
                      sx={{
                        "& .MuiFilledInput-root": {
                          backgroundColor: "rgba(255, 255, 255, 0.95)",
                          "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 1)",
                          },
                        },
                      }}
                    />
                  </Box>
                )}

                <Box sx={{ display: "flex", gap: 1 }}>
                  <TextField
                    ref={refs.cEmail}
                    disabled={notaryPaymentLoading}
                    size="small"
                    error={!!(formik.touched.cEmail && formik.errors.cEmail)}
                    fullWidth
                    helperText={formik.touched.cEmail && formik.errors.cEmail}
                    name="cEmail"
                    value={formik.values.cEmail}
                    onChange={formik.handleChange}
                    label="Email"
                    variant="filled"
                    sx={{
                      "& .MuiFilledInput-root": {
                        backgroundColor: "rgba(255, 255, 255, 1)",
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 1)",
                        },
                      },
                    }}
                  />
                  <TextField
                    ref={refs.cMobile}
                    disabled={notaryPaymentLoading}
                    size="small"
                    error={!!(formik.touched.cMobile && formik.errors.cMobile)}
                    fullWidth
                    helperText={formik.touched.cMobile && formik.errors.cMobile}
                    name="cMobile"
                    value={formik.values.cMobile}
                    label="Mobile"
                    variant="filled"
                    onChange={(e) => handleNewPhoneChange(e)}
                    sx={{
                      "& .MuiFilledInput-root": {
                        backgroundColor: "rgba(255, 255, 255, 1)",
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 1)",
                        },
                      },
                    }}
                  />
                </Box>
              </Stack>

              {/* Signing Order Button */}
              {(ownerSigning?.notarizationNeeded ||
                ownerSigning?.signingNeeded) &&
                (contractorSigning?.notarizationNeeded ||
                  contractorSigning?.signingNeeded) && (
                  <Box
                    sx={{
                      mt: "auto",
                      pt: 2,
                      display: "flex",
                      justifyContent: "flex-start",
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    <Button
                      disabled={
                        notaryPaymentLoading ||
                        selectedRow?.documents?.noc_affidavit?.is_created ===
                          true
                      }
                      onClick={() => setSigningOrder("contractor")}
                      variant={
                        signingOrder === "contractor" ? "contained" : "outlined"
                      }
                      size="medium"
                      fullWidth
                      sx={{
                        textTransform: "none",
                        borderRadius: "8px",
                        px: 2,
                        py: 0.75,
                        fontWeight:
                          signingOrder === "contractor" ? "bold" : "medium",
                        backgroundColor:
                          signingOrder === "contractor"
                            ? "primary.main"
                            : "transparent",
                        color:
                          signingOrder === "contractor"
                            ? "white"
                            : "primary.main",
                        borderColor: "primary.main",
                        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                        "&:hover": {
                          backgroundColor:
                            signingOrder === "contractor"
                              ? "primary.dark"
                              : "rgba(25, 118, 210, 0.08)",
                          color:
                            signingOrder === "contractor"
                              ? "white"
                              : "primary.main",
                          borderColor:
                            signingOrder === "contractor"
                              ? "primary.dark"
                              : "primary.main",
                        },
                      }}
                    >
                      Contractor Sign/Notarize First
                    </Button>
                  </Box>
                )}
            </Card>
          </Grid>
        ) : null}

        <Grid
          size={{
            sm: 12,
            md:
              contractorSigning?.notarizationNeeded ||
              contractorSigning?.signingNeeded
                ? 6
                : 12,
          }}
        >
          {ownerSigning?.notarizationNeeded || ownerSigning?.signingNeeded ? (
            <Card
              sx={{
                p: 3,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                background:
                  signingOrder === "owner"
                    ? "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)"
                    : "linear-gradient(135deg, #fafbfc 0%, #f0f2f5 100%)",
                borderRadius: "20px",
                border: "1px solid rgba(25, 118, 210, 0.1)",
                boxShadow:
                  "0 8px 24px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(25, 118, 210, 0.05) inset",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    signingOrder === "owner"
                      ? "linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(25, 118, 210, 0.05) 100%)"
                      : "linear-gradient(135deg, rgba(25, 118, 210, 0.03) 0%, rgba(25, 118, 210, 0) 100%)",
                  pointerEvents: "none",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                },
                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  boxShadow:
                    "0 12px 32px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(25, 118, 210, 0.1) inset",
                },
              }}
            >
              <Typography
                fontWeight="bold"
                align="center"
                mb={3}
                variant="h5"
                sx={{ color: "primary.main", position: "relative", zIndex: 1 }}
              >
                Owner/Agent
              </Typography>

              <Stack spacing={2}>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <TextField
                    ref={refs.oFirst}
                    disabled={notaryPaymentLoading}
                    size="small"
                    error={!!(formik.touched.oFirst && formik.errors.oFirst)}
                    fullWidth
                    helperText={formik.touched.oFirst && formik.errors.oFirst}
                    name="oFirst"
                    value={formik.values.oFirst}
                    onChange={formik.handleChange}
                    label="First Name"
                    variant="filled"
                  />
                  <TextField
                    disabled={notaryPaymentLoading}
                    size="small"
                    error={!!(formik.touched.oMiddle && formik.errors.oMiddle)}
                    fullWidth
                    helperText={formik.touched.oMiddle && formik.errors.oMiddle}
                    name="oMiddle"
                    value={formik.values.oMiddle}
                    onChange={formik.handleChange}
                    label="Middle Name"
                    variant="filled"
                  />
                  <TextField
                    ref={refs.oLast}
                    disabled={notaryPaymentLoading}
                    size="small"
                    error={!!(formik.touched.oLast && formik.errors.oLast)}
                    fullWidth
                    helperText={formik.touched.oLast && formik.errors.oLast}
                    name="oLast"
                    value={formik.values.oLast}
                    onChange={formik.handleChange}
                    label="Last Name"
                    variant="filled"
                    sx={{
                      "& .MuiFilledInput-root": {
                        backgroundColor: "rgba(255, 255, 255, 1)",
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 1)",
                        },
                      },
                    }}
                  />
                </Box>

                {ownerSigning?.notarizationNeeded && (
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <TextField
                      ref={refs.oAddress}
                      disabled={notaryPaymentLoading}
                      size="small"
                      error={
                        !!(formik.touched.oAddress && formik.errors.oAddress)
                      }
                      fullWidth
                      helperText={
                        formik.touched.oAddress && formik.errors.oAddress
                      }
                      name="oAddress"
                      value={formik.values.oAddress}
                      onChange={formik.handleChange}
                      label="Address"
                      variant="filled"
                      sx={{
                        "& .MuiFilledInput-root": {
                          backgroundColor: "rgba(255, 255, 255, 0.95)",
                          "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 1)",
                          },
                        },
                      }}
                    />
                    <TextField
                      ref={refs.oCity}
                      disabled={notaryPaymentLoading}
                      size="small"
                      error={!!(formik.touched.oCity && formik.errors.oCity)}
                      fullWidth
                      helperText={formik.touched.oCity && formik.errors.oCity}
                      name="oCity"
                      value={formik.values.oCity}
                      onChange={formik.handleChange}
                      label="City"
                      variant="filled"
                      sx={{
                        "& .MuiFilledInput-root": {
                          backgroundColor: "rgba(255, 255, 255, 0.95)",
                          "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 1)",
                          },
                        },
                      }}
                    />
                    <TextField
                      ref={refs.oZip}
                      disabled={notaryPaymentLoading}
                      size="small"
                      error={!!(formik.touched.oZip && formik.errors.oZip)}
                      fullWidth
                      helperText={formik.touched.oZip && formik.errors.oZip}
                      name="oZip"
                      value={formik.values.oZip}
                      onChange={formik.handleChange}
                      label="ZIP"
                      variant="filled"
                      sx={{
                        "& .MuiFilledInput-root": {
                          backgroundColor: "rgba(255, 255, 255, 0.95)",
                          "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 1)",
                          },
                        },
                      }}
                    />
                  </Box>
                )}

                <Box sx={{ display: "flex", gap: 1 }}>
                  <TextField
                    ref={refs.oEmail}
                    disabled={notaryPaymentLoading}
                    size="small"
                    error={!!(formik.touched.oEmail && formik.errors.oEmail)}
                    fullWidth
                    helperText={formik.touched.oEmail && formik.errors.oEmail}
                    name="oEmail"
                    value={formik.values.oEmail}
                    onChange={formik.handleChange}
                    label="Email"
                    variant="filled"
                    sx={{
                      "& .MuiFilledInput-root": {
                        backgroundColor: "rgba(255, 255, 255, 1)",
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 1)",
                        },
                      },
                    }}
                  />
                  <TextField
                    ref={refs.oMobile}
                    disabled={notaryPaymentLoading}
                    size="small"
                    error={!!(formik.touched.oMobile && formik.errors.oMobile)}
                    fullWidth
                    helperText={formik.touched.oMobile && formik.errors.oMobile}
                    name="oMobile"
                    value={formik.values.oMobile}
                    label="Mobile"
                    variant="filled"
                    onChange={(e) => handleNewPhoneChange(e)}
                    sx={{
                      "& .MuiFilledInput-root": {
                        backgroundColor: "rgba(255, 255, 255, 1)",
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 1)",
                        },
                      },
                    }}
                  />
                </Box>

                <FormControlLabel
                  control={
                    <Checkbox
                      name="oMobileConsent"
                      checked={formik.values.oMobileConsent}
                      onChange={formik.handleChange}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      I confirm I have the homeowner's permission to send text
                      messages to the mobile number above. Data processing
                      follows our{" "}
                      <Link
                        href="https://permitrockstar.com/privacy-policy/"
                        target="_blank"
                        rel="noopener noreferrer"
                        color="primary"
                        sx={{ textDecoration: "underline", fontWeight: "bold" }}
                      >
                        Privacy Policy
                      </Link>
                      .
                    </Typography>
                  }
                />

                {/* <Typography
                  variant="body2"
                  color="primary"
                  fontWeight="bold"
                  align="center"
                  sx={{ mt: 1 }}
                >
                  {ownerSigning.notarizationNeeded
                    ? "All other information will be gathered from the signer. Please ensure that the contact information you enter is correct."
                    : "Please ensure that you enter the owner's full personal name and other required details accurately, as this information will be used to initiate the signing process."}
                </Typography> */}
              </Stack>

              {/* Signing Order Button */}
              {(ownerSigning?.notarizationNeeded ||
                ownerSigning?.signingNeeded) &&
                (contractorSigning?.notarizationNeeded ||
                  contractorSigning?.signingNeeded) && (
                  <Box
                    sx={{
                      mt: "auto",
                      pt: 2,
                      display: "flex",
                      justifyContent: "flex-start",
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    <Button
                      disabled={notaryPaymentLoading}
                      onClick={() => setSigningOrder("owner")}
                      variant={
                        signingOrder === "owner" ? "contained" : "outlined"
                      }
                      size="medium"
                      fullWidth
                      sx={{
                        textTransform: "none",
                        borderRadius: "8px",
                        px: 2,
                        py: 0.75,
                        fontWeight:
                          signingOrder === "owner" ? "bold" : "medium",
                        backgroundColor:
                          signingOrder === "owner"
                            ? "primary.main"
                            : "transparent",
                        color:
                          signingOrder === "owner" ? "white" : "primary.main",
                        borderColor: "primary.main",
                        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                        "&:hover": {
                          backgroundColor:
                            signingOrder === "owner"
                              ? "primary.dark"
                              : "rgba(25, 118, 210, 0.08)",
                          color:
                            signingOrder === "owner" ? "white" : "primary.main",
                          borderColor:
                            signingOrder === "owner"
                              ? "primary.dark"
                              : "primary.main",
                        },
                      }}
                    >
                      Owner/Agent Sign/Notarize First
                    </Button>
                  </Box>
                )}
            </Card>
          ) : (
            <Card
              sx={{
                p: 3,
                textAlign: "center",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="body2" color="primary" fontWeight="bold">
                {contractorSigning?.notarizationNeeded ||
                contractorSigning?.signingNeeded
                  ? contractorSigning?.notarizationNeeded
                    ? "All other information will be gathered from the signer. Please ensure that the contact information you enter is correct."
                    : "Please ensure that you enter the contractor's full personal name and other required details accurately, as this information will be used to initiate the signing process."
                  : "No additional signer information is required."}
              </Typography>
            </Card>
          )}
        </Grid>
      </Grid>
    </AnimatedStep>
  );

  const renderPaymentStep = () => (
    <AnimatedStep>
      <Typography
        variant="h6"
        align="center"
        color="primary"
        fontWeight="bold"
        mb={2}
      >
        Payment & Confirmation
      </Typography>

      {/* IPEN PIN Display */}
      {notarizationType === "ipen" && ipenPin && (
        <Card
          sx={{ p: 2, bgcolor: "#e3f2fd", border: "2px solid #2196f3", mb: 3 }}
        >
          <Typography
            variant="subtitle1"
            color="primary"
            fontWeight="bold"
            align="center"
            mb={1}
          >
            📌 Verification PIN
          </Typography>
          <Typography
            variant="h3"
            sx={{
              fontFamily: "monospace",
              letterSpacing: 4,
              textAlign: "center",
              color: "#1976d2",
              fontWeight: "bold",
              mb: 1,
            }}
          >
            {ipenPin}
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            Provide this PIN to your notary to verify physical presence
          </Typography>
        </Card>
      )}

      {/* Mixed Session Warning */}
      {notarizationType === "ipen" &&
        ownerSigning.notarizationNeeded &&
        contractorSigning.notarizationNeeded && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              ℹ️ <strong>Note:</strong> Since both owner and contractor
              notarization is required, contractor documents will use IPEN while
              owner documents will be processed separately via RON.
            </Typography>
          </Alert>
        )}

      {/* Payment Section */}
      {!selectedRow?.payments?.vip?.paid && (
        <Grid container spacing={3}>
          <Grid size={{ sm: 12, md: 6 }}>
            <Card
              sx={{
                p: 3,
                height: "100%",
                background: "linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%)",
                borderRadius: "20px",
                border: "1px solid rgba(25, 118, 210, 0.1)",
                boxShadow:
                  "0 8px 24px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(25, 118, 210, 0.05) inset",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    "linear-gradient(135deg, rgba(25, 118, 210, 0.03) 0%, rgba(25, 118, 210, 0) 100%)",
                  pointerEvents: "none",
                },
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  boxShadow:
                    "0 12px 32px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(25, 118, 210, 0.1) inset",
                },
              }}
            >
              <Typography
                align="center"
                variant="h5"
                mb={3}
                sx={{
                  color: "primary.main",
                  fontWeight: "bold",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                {currentUser?.user?.role !== "admin"
                  ? "Select Payment Card"
                  : "No Payment Card Required"}
              </Typography>

              {paymentCards?.length === 0 &&
              currentUser?.user?.role !== "admin" &&
              !currentUser?.user?.admin ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    color="error"
                    mb={2}
                  >
                    You do not have any payment method saved. Please click the
                    button below to navigate to the payments page and set up a
                    payment method.
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={async () => {
                      await dispatch(
                        setModalsState({ addPaymentModalOpen: true }),
                      );
                    }}
                  >
                    Set up card payment
                  </Button>
                </Box>
              ) : (
                <Box sx={{ position: "relative", zIndex: 1 }}>
                  <CreditCardDisplay
                    cards={paymentCards || []}
                    activeStep={activeStep}
                    setActiveStep={setActiveStep}
                  />
                </Box>
              )}
            </Card>
          </Grid>

          <Grid size={{ sm: 12, md: 6 }}>
            <Card
              sx={{
                p: 3,
                height: "100%",
                background: "linear-gradient(135deg, #fafbfc 0%, #f0f2f5 100%)",
                borderRadius: "20px",
                border: "1px solid rgba(25, 118, 210, 0.1)",
                boxShadow:
                  "0 8px 24px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(25, 118, 210, 0.05) inset",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    "linear-gradient(135deg, rgba(25, 118, 210, 0.03) 0%, rgba(25, 118, 210, 0) 100%)",
                  pointerEvents: "none",
                },
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  boxShadow:
                    "0 12px 32px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(25, 118, 210, 0.1) inset",
                },
              }}
            >
              <Typography
                variant="h5"
                align="center"
                sx={{
                  fontWeight: "bold",
                  mb: 3,
                  color: "primary.main",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                Total Cost
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                  p: 3,
                  borderRadius: "16px",
                  bgcolor: "rgba(255, 255, 255, 1)",
                  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: "bold",
                    color: "primary.main",
                    textDecoration:
                      confirmedPricing?.has_discount ||
                      tempAmount ||
                      currentUser?.user?.role === "admin" ||
                      currentUser?.user?.admin === true
                        ? "line-through"
                        : "none",
                  }}
                >
                  {formatMoneyValue(
                    signOnly
                      ? currentUser?.service_tiers?.sign_only
                      : confirmedPricing?.original_price ||
                          (docNotaryTypeValue === 0
                            ? currentUser?.service_tiers?.notary_record
                            : docNotaryTypeValue === 1
                              ? currentUser?.service_tiers?.noc_notary
                              : currentUser?.service_tiers
                                  ?.permit_package_notary),
                  )}
                </Typography>
                {confirmedPricing?.has_discount ? (
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: "bold", color: "success.main", mt: 1 }}
                  >
                    {formatMoneyValue(confirmedPricing.final_price)}
                  </Typography>
                ) : tempAmount &&
                  currentUser?.user?.role !== "admin" &&
                  !currentUser?.user?.admin ? (
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: "bold", color: "success.main", mt: 1 }}
                  >
                    {tempAmount}
                  </Typography>
                ) : currentUser?.user?.role === "admin" ||
                  currentUser?.user?.admin === true ? (
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: "bold", color: "success.main", mt: 1 }}
                  >
                    $0.00
                  </Typography>
                ) : null}
                <Typography
                  align="center"
                  variant="body2"
                  sx={{ color: "text.secondary", mt: 2 }}
                >
                  {(() => {
                    const nocInSelection = (
                      selectedRow?.selected_documents || []
                    ).some(
                      (doc) =>
                        doc?.name === "noc" || doc?.name === "noc_affidavit",
                    );
                    if (signOnly) {
                      return "This is the total amount for Signing for all documents";
                    } else {
                      if (docNotaryTypeValue === 0) {
                        return `This is the total amount for Notarization${nocInSelection ? " & Recording" : ""} for all documents`;
                      }
                      if (docNotaryTypeValue === 1) {
                        return "This is the total amount for Notarization & Recording for Notice of Commencement Form";
                      }
                    }

                    return "This is the total amount for Notarization for all permit documents, excluding the Notice of Commencement";
                  })()}
                </Typography>
              </Box>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Auto-Start Recording Option */}
      {docNotaryTypeValue !== 2 &&
        (() => {
          const nocInSelection = (selectedRow?.selected_documents || []).some(
            (doc) => doc?.name === "noc" || doc?.name === "noc_affidavit",
          );
          return nocInSelection;
        })() && (
          <Card sx={{ p: 2, mt: 5 }}>
            <FormControlLabel
              disabled={notaryPaymentLoading}
              control={
                <Switch
                  checked={autoStartRecording}
                  onChange={(e) => setAutoStartRecording(e.target.checked)}
                />
              }
              label="Automatically start recording process after notarization"
            />
          </Card>
        )}
    </AnimatedStep>
  );

  // Helper function to handle post-payment actions
  const handlePostPayment = async (resp, helpers) => {
    if (resp?.data?.success && resp?.data?.need_assign) {
      // Auto-switch to "Packages Sent to Contractors" tab when package is assigned
      dispatch(setActiveTab(1));

      const result = await dispatch(
        getPermitPackages({ newPage: 0, newPageSize: 5, customActiveTab: 1 }),
      );
      dispatch(setSelectedRow(null));
      dispatch(setPage(0));
      dispatch(setRowCount(result?.data || 0));
      setSnackOpen(true);
    } else if (resp?.data?.success && !resp?.data?.need_assign) {
      await dispatch(updatePermitPackage(resp?.data?.data));
      setSnackOpen(true);
      setOpen(false);

      if (
        !resp?.data?.onlyOwner &&
        resp?.data?.data?.processing_permit?.notarization?.signing_order !==
          "owner"
      ) {
        setRonScheduleOpen(true);
      }
      setIsAcknowledged(false);
      helpers?.resetForm();
    }
  };

  // Get current step content
  const getCurrentStepContent = () => {
    const currentStep = wizardSteps[currentWizardStep];
    if (!currentStep) return renderOverviewStep();

    switch (currentStep.id) {
      case "overview":
        return renderOverviewStep();
      case "method":
        return renderMethodStep();
      case "notary":
        return renderNotaryStep();
      case "services":
        return renderServicesStep();
      case "information":
        return renderInformationStep();
      case "payment":
        return renderPaymentStep();
      default:
        return renderOverviewStep();
    }
  };

  return (
    <AnimatedModal
      open={open}
      onClose={() => {
        // setOpen(false);
        // resetModalState(formik);
      }}
    >
      <ModalContainer className="modal-materialize">
        {/* Header */}
        <ModalHeader>
          <IconButton
            disabled={notaryPaymentLoading}
            onClick={() => {
              setOpen(false);
              resetModalState(formik);
              setCurrentWizardStep(0);
              setOwnerIpenNotaryId("");
              setNotarizationType("ron");
              setNotarySource("permitRockstar");
              setIpenPin("");
              setOwnerNotarizationType("ron");
              setContractorNotarizationType("ron");
              setContractorIpenNotaryId("");
              setContractorSettings({}); // Reset all contractor settings
              // Reset refs when modal closes
              notarizationTypeRef.current = "ron";
              ownerNotarizationTypeRef.current = "ron";
              notarySourceRef.current = "permitRockstar";
              ipenPinRef.current = "";
              ownerIpenNotaryIdRef.current = "";
              setPlanReviewService({
                usePermitRockstar: false,
                useOrganization: false,
                customProvider: "",
              });
              setInspectionService({
                usePermitRockstar: false,
                useOrganization: false,
                customProvider: "",
                inspectionTypes: [],
              });
              setOwnerSigning({
                notarizationNeeded: false,
                signingNeeded: false,
              });
              setContractorSigning({
                notarizationNeeded: false,
                signingNeeded: false,
              });
              setLoadingNotaries(false);
              setSignOnly(false);
              setConfirmedPricing(null);
            }}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "inherit",
              zIndex: 10,
              padding: 1,
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>

          <Typography
            variant="h5"
            align="center"
            fontWeight="bold"
            sx={{ mb: 0.5, position: "relative", zIndex: 1 }}
          >
            Notarization & Signing Portal
          </Typography>
          <Typography
            variant="body2"
            align="center"
            sx={{ opacity: 0.9, position: "relative", zIndex: 1, mb: 1.5 }}
          >
            Complete your document signing and notarization in minutes
          </Typography>

          <Paper
            sx={{
              p: 1,
              bgcolor: "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(10px)",
              borderRadius: 1.5,
              position: "relative",
              zIndex: 1,
            }}
          >
            <Typography
              variant="body2"
              align="center"
              fontWeight="600"
              sx={{ color: "#ffb74d" }}
            >
              {docNotaryTypeValue === 0
                ? "All Documents Selected"
                : docNotaryTypeValue === 1 &&
                    selectedRow?.documents?.noc_affidavit?.is_created === true
                  ? "Notice of Commencement + Affidavit"
                  : docNotaryTypeValue === 1
                    ? "Notice of Commencement Only"
                    : "All Permit Forms (Excluding NOC)"}
            </Typography>
          </Paper>
        </ModalHeader>

        {/* Progress Indicator */}
        <ProgressIndicator>
          <Stepper
            activeStep={currentWizardStep}
            orientation="horizontal"
            sx={{ width: "100%" }}
          >
            {wizardSteps.map((step, index) => (
              <Step key={step.id}>
                <StepLabel
                  StepIconComponent={({ active, completed }) => (
                    <StepIcon
                      completed={completed}
                      active={active}
                      onClick={() => handleStepClick(index)}
                    >
                      {completed ? <CheckCircleIcon /> : step.icon}
                    </StepIcon>
                  )}
                  onClick={() => handleStepClick(index)}
                  sx={{ cursor: "pointer" }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight:
                        currentWizardStep === index ? "bold" : "normal",
                    }}
                  >
                    {step.title}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </ProgressIndicator>

        {/* Main Content */}
        <Box
          sx={{
            p: 2,
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {/* Step Content */}
          <Box>{getCurrentStepContent()}</Box>
        </Box>

        {/* Navigation */}
        <Divider />

        {/* Declaration Section - Only show on payment step */}
        {wizardSteps[currentWizardStep]?.id === "payment" && (
          <Box sx={{ px: 2, pt: 2, pb: 1 }}>
            <FormControlLabel
              disabled={notaryPaymentLoading}
              control={
                <Checkbox
                  color="primary"
                  checked={isAcknowledged}
                  onChange={(event) => setIsAcknowledged(event.target.checked)}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: "text.primary" }}>
                  I acknowledge that I have reviewed the forms I created, and
                  understand that once notarization/signing begins, no further
                  modifications can be made to the documents.
                </Typography>
              }
            />
          </Box>
        )}

        {submitError && (
          <Typography
            variant="body2"
            color="error"
            pt={1}
            sx={{ fontWeight: "bold", textAlign: "center" }}
          >
            {submitError}
          </Typography>
        )}

        <Box
          sx={{
            p: 1.5,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", sm: "center" },
            gap: 1.5,
            flexShrink: 0,
          }}
        >
          <FloatingActionButton
            variant="outlined"
            disabled={currentWizardStep === 0 || notaryPaymentLoading}
            onClick={handlePrevious}
            startIcon={<ArrowBackIcon />}
          >
            Previous
          </FloatingActionButton>

          {/* Document Review Link */}
          {wizardSteps[currentWizardStep]?.id === "payment" && (
            <Box>
              <FloatingActionButton
                variant="text"
                onClick={() => setReviewDocModalOpen(true)}
              >
                📄 Review documents
              </FloatingActionButton>
            </Box>
          )}

          <FloatingActionButton
            variant="contained"
            disabled={
              (wizardSteps[currentWizardStep]?.id === "services" &&
                ((ownerSigning?.notarizationNeeded &&
                  ownerNotarizationType === "ipen" &&
                  hasOrgNotaries &&
                  !ownerIpenNotaryId) ||
                  (contractorSigning?.notarizationNeeded &&
                    hasOrgNotaries &&
                    requiredContractors.some((contractor) => {
                      const license = contractor?.licenseNumber;
                      if (!license) return false;
                      const settings = contractorSettings[license];
                      return (
                        settings?.notarizationType === "ipen" &&
                        !settings?.ipenNotaryId
                      );
                    })))) ||
              (wizardSteps[currentWizardStep]?.id === "information" &&
                !isSignerInformationValid()) ||
              (wizardSteps[currentWizardStep]?.id === "payment" &&
                !isAcknowledged) ||
              notaryPaymentLoading ||
              (currentUser?.user?.role !== "admin" &&
                !currentUser?.user?.admin &&
                paymentCards.length === 0 &&
                wizardSteps[currentWizardStep]?.id === "payment")
            }
            onClick={async () => {
              if (currentWizardStep < wizardSteps.length - 1) {
                if (wizardSteps[currentWizardStep]?.id === "information") {
                  const errors = await formik?.validateForm();
                  if (errors && Object.keys(errors).length > 0) {
                    // Set errors in formik so TextFields can display them
                    formik.setErrors(errors);
                    // Mark all fields with errors as touched so they show error state
                    const touchedFields = {};
                    Object.keys(errors).forEach((field) => {
                      touchedFields[field] = true;
                    });
                    formik.setTouched(touchedFields);
                    scrollToFirstError(errors);
                    // setSubmitError(
                    //   `Some fields are incomplete: ${Object.keys(errors).join(", ")}. Please review the form carefully before proceeding.`
                    // );
                    return;
                  }
                }
                handleNext();
              } else {
                // Final submission
                const errors = await formik?.validateForm();
                if (errors && Object.keys(errors).length > 0) {
                  // Set errors in formik so TextFields can display them
                  formik.setErrors(errors);
                  // Mark all fields with errors as touched so they show error state
                  const touchedFields = {};
                  Object.keys(errors).forEach((field) => {
                    touchedFields[field] = true;
                  });
                  formik.setTouched(touchedFields);
                  scrollToFirstError(errors);
                  setSubmitError(
                    `Some fields are incomplete: ${Object.keys(errors).join(", ")}. Please review the form carefully before proceeding.`,
                  );
                  return;
                }
                formik.handleSubmit();
              }
            }}
            loading={notaryPaymentLoading}
            endIcon={
              currentWizardStep < wizardSteps.length - 1 ? (
                <ArrowForwardIcon />
              ) : (
                <SendIcon />
              )
            }
          >
            {currentWizardStep === wizardSteps.length - 1
              ? !selectedRow?.payments?.vip?.paid &&
                !ownerSigning.notarizationNeeded &&
                !contractorSigning.notarizationNeeded
                ? "Submit Document(s) Signing"
                : !selectedRow?.payments?.vip?.paid &&
                    (ownerSigning.notarizationNeeded ||
                      contractorSigning.notarizationNeeded)
                  ? "Submit Notarization Request"
                  : "Submit Purchase"
              : "Next"}
          </FloatingActionButton>
        </Box>
      </ModalContainer>
    </AnimatedModal>
  );
}

export default React.memo(StartNotarizationModal);
